// ===================================================================
// data/conversationsData.ts
// Conversations interactives style Duolingo — Q&R contextuelles
// ===================================================================

export interface ConversationOption {
  id: string;
  text: string;
  translation: string;
  translationEn?: string;
  isCorrect: boolean;
}

export interface ConversationStep {
  id: string;
  message: string;
  translation: string;
  translationEn?: string;
  hint?: string;
  options: ConversationOption[];
  correctReaction?: string;
}

export interface LexiconEntry {
  bambara: string;
  french: string;
  english?: string;
}

export interface Conversation {
  id: string;
  title: string;
  context: string;
  icon: string;
  difficulty: 'débutant' | 'intermédiaire' | 'avancé';
  character: { name: string; emoji: string };
  steps: ConversationStep[];
  xpReward: number;
  lexicon?: LexiconEntry[];
}

// ─── Wolof ────────────────────────────────────────────────────────────────────
export const WOLOF_CONVERSATIONS: Conversation[] = [



  // ── Se présenter ───────────────────────────────────────────────────────────

  // ── Prendre un taxi ────────────────────────────────────────────────────────



  // ── Au restaurant ──────────────────────────────────────────────────────────
  






  // ── À la téléphonie ───────────────────────────────────────────────────────


  // ── À la plage ────────────────────────────────────────────────────────────






  // ── Demander son chemin ───────────────────────────────────────────────────
  // Sources : Senego.com (directions : fi/foofu/ci kanam/ci ginnaaw/ci ndeyjoor/ci cëmm),
  //           Diouf pratique (fan la nekk = où est-il),
  //           Jangawolof.org ("fan la X nekk" = où se trouve X),
  //           Dictionnaire pratique Scribd (fu oteel bi nekk, fu gaar bi nekk)

];

export const BAMBARA_CONVERSATIONS: Conversation[] = [

  // ── Au marché ──────────────────────────────────────────────────────────────
  //
  // Sources vérifiées :
  //   • "I ni sogoma" = bonjour (matin) — cultures-maliennes.over-blog.com ✅
  //   • "Nba" = oui / merci (réponse masculine) — djasso.com ✅
  //   • "I ka kɛnɛ wa ?" = tu vas bien ? — Wikibooks Bambara/Salutations ✅
  //   • "N ka kɛnɛ" = je vais bien — Wikibooks ✅
  //   • "i ni ce" = merci/bonjour (neutre) — Wikibooks ✅
  //   • "Mun bé i fɛ ?" = qu'est-ce que tu veux ? — Ankataa (N bɛ ji fɛ = structure) ✅
  //   • "N bé kini ni jɛgɛ fɛ" = je veux riz et poisson — Ankataa ✅
  //   • CORRECTION s4 : logique inversée corrigée — le client répond à la quantité
  //     AVANT de demander le prix, pour une progression naturelle.
  //   • "kɛmɛ fila" = 1000 CFA (kɛmɛ = 500 CFA en usage monétaire malien)
  //     — wikivoyage.org/wiki/Guide_linguistique_bambara ✅
  //   • "Aw ni baara" = merci/au revoir — Ankataa ✅


{
    id: 'salutations-ba',
    title: 'Salutations',
    context: `👋 Tu rencontres quelqu'un à Bamako.`,
    icon: '👋',
    difficulty: 'débutant',
    character: { name: 'Fanta', emoji: '👩🏿' },
    xpReward: 25,
    steps: [
      {
        id: 's1',
        // CORRECTION : "I tɔgɔ ye mun ?" → "I tɔgɔ ye di ?" (forme standard attestée Wikipedia bambara)
        message: 'I ni sogoma Moussa ',
        translation: "Bonjour Moussa",
        options: [
          { id: 'a', text: "Nbaa ! Here sira wa ?", translation: "Ca va ", isCorrect: true },
          { id: 'b', text: "N bé taa.", translation: "Je pars.", isCorrect: false },
          { id: 'c', text: "Kɛnɛ.", translation: "Ça va.", isCorrect: false },
          { id: 'd', text: "N bé kini lɔn.", translation: "Je connais le riz.", isCorrect: false },
        ],
        // Réaction : compliment sur le nom + curiosité sur l'origine
        correctReaction: 'Tɔgɔ ɲuman ! Nba, i bé bɔ min ?',
      },
      {
        id: 's2',
        message: 'Hɛɛrɛ ',
        translation: "Tu viens d'où ?",
        options: [
          { id: 'a', text: "Somɔgɔw do ?", translation: "Ca va", isCorrect: true },
          { id: 'b', text: "N bé sigi Bamako.", translation: "J'habite à Bamako.", isCorrect: false },
          { id: 'c', text: "N bé kalan kɛ.", translation: "J'étudie.", isCorrect: false },
          { id: 'd', text: "N bé dɔn.", translation: "Je danse.", isCorrect: false },
        ],
        // Réaction de surprise positive + question sur la langue
        correctReaction: 'Faransi — fɛn ɲuman ye ! I bé bamanankan fɔ wa ?',
      },
      {
        id: 's3',
        message: 'O ka kɛnɛ !',
        translation: "Tu parles bambara ?",
        options: [
          { id: 'a', text: "Denmisɛnw do ?", translation: "Ils vont bien !", isCorrect: true },
          { id: 'b', text: "N bé fɔ kosɛbɛ.", translation: "Je parle beaucoup.", isCorrect: false },
          { id: 'c', text: "Kɛnɛ dɔn.", translation: "C'est bon.", isCorrect: false },
          { id: 'd', text: "Ayi, n tɛ fɔ.", translation: "Non, je ne parle pas.", isCorrect: false },
        ],
        // Encouragement et curiosité sur le séjour
        correctReaction: 'Ɲuman kosɛbɛ ! Kalan kɛ ɲuman ye. I bé sigi Bamako tuma jɔli ?',
      },
      {
        id: 's4',
        message: 'O ka kɛnɛ !',
        translation: "Tu restes à Bamako combien de temps ?",
        options: [
          { id: 'a', text: "Lukɔnɔmɔgɔw bɛɛ ", translation: "Il vont bien !", isCorrect: true },
          { id: 'b', text: "N bé taa sugu.", translation: "Je vais au marché.", isCorrect: false },
          { id: 'c', text: "Bi kɛra di.", translation: "La journée était bonne.", isCorrect: false },
          { id: 'd', text: "Bamako ka ɲi.", translation: "Bamako est bien.", isCorrect: false },
        ],
        // Transition vers une question sur l'âge
        correctReaction: 'Kalo fila — malɔ ! San jɔli bé i la ?',
      },
      {
        id: 's5',
        message: 'O be ka kɛnɛ, E fana ka Somɔgɔw do ?',
        translation: "Quel âge as-tu ?",
        options: [
          { id: 'a', text: "O ka kɛnɛ ", translation: "J'ai 25 ans.", isCorrect: true },
          { id: 'b', text: "N bé taa sugu.", translation: "Je vais au marché.", isCorrect: false },
          { id: 'c', text: "Bi kɛra di.", translation: "La journée était bonne.", isCorrect: false },
          { id: 'd', text: "I ni wula !", translation: "Bonsoir !", isCorrect: false },
        ],
        // Réaction chaleureuse avant la conclusion
        correctReaction: 'Kɛnɛ ! An bé k\'an bɛn sɔɔni !',
      },
      {
        id: 's6',
        message: 'E ka muso n\'i ka denmisɛnw  do ?',
        translation: "Ravi de te rencontrer ! Bonne route !",
        options: [
          { id: 'a', text: "O be ka kɛnɛ", translation: "Moi aussi ! À bientôt !", isCorrect: true },
          { id: 'b', text: "N bé taa.", translation: "Je pars.", isCorrect: false },
          { id: 'c', text: "Bɔlen.", translation: "C'est parti.", isCorrect: false },
          { id: 'd', text: "N ka sigi.", translation: "Je reste.", isCorrect: false },
        ],
        correctReaction: '👏 Tu sais te présenter en bambara !',
      },

      {
        id: 's7',
        message: 'E ka muso n\'i ka denmisɛnw  do ?',
        translation: "Ravi de te rencontrer ! Bonne route !",
        options: [
          { id: 'a', text: "O be ka kɛnɛ", translation: "Moi aussi ! À bientôt !", isCorrect: true },
          { id: 'b', text: "N bé taa.", translation: "Je pars.", isCorrect: false },
          { id: 'c', text: "Bɔlen.", translation: "C'est parti.", isCorrect: false },
          { id: 'd', text: "N ka sigi.", translation: "Je reste.", isCorrect: false },
        ],
        correctReaction: '👏 Tu sais te présenter en bambara !',
      },

      {
        id: 's8',
        message: 'E ka muso n\'i ka denmisɛnw  do ?',
        translation: "Ravi de te rencontrer ! Bonne route !",
        options: [
          { id: 'a', text: "O be ka kɛnɛ", translation: "Moi aussi ! À bientôt !", isCorrect: true },
          { id: 'b', text: "N bé taa.", translation: "Je pars.", isCorrect: false },
          { id: 'c', text: "Bɔlen.", translation: "C'est parti.", isCorrect: false },
          { id: 'd', text: "N ka sigi.", translation: "Je reste.", isCorrect: false },
        ],
        correctReaction: '👏 Tu sais te présenter en bambara !',
      },
    ],
  },


  // ── Salutations en famille · sentier unité 1 (Famille → Animaux) ────────
  {
    id: 'famille-animaux-ba',
    title: 'Salutations entre amis',
    context: '🤝 Tu es Bakary. Ton ami Moussa te croise dans la rue à Bamako. Il te demande des nouvelles de toute ta famille.',
    icon: '🤝',
    difficulty: 'débutant',
    character: { name: 'Moussa', emoji: '🧑🏿' },
    xpReward: 50,
    lexicon: [
      { bambara: 'I ni sogoma', french: 'Bonjour (le matin)' },
      { bambara: 'I ka kɛnɛ wa ?', french: 'Tu vas bien ?' },
      { bambara: 'Ne ka kɛnɛ', french: 'Je vais bien' },
      { bambara: 'Tɔrɔ tɛ', french: 'Pas de problème / ça va' },
      { bambara: 'somɔgɔw', french: 'la famille' },
      { bambara: 'fa', french: 'père' },
      { bambara: 'ba', french: 'mère' },
      { bambara: 'cɛkɔrɔba', french: 'grand-père' },
      { bambara: 'musokɔrɔba', french: 'grand-mère' },
      { bambara: 'deenw', french: 'les enfants' },
      { bambara: 'muso', french: 'femme / épouse' },
      { bambara: 'dɔgɔkɔw', french: 'les cadets / jeunes frères-sœurs' },
      { bambara: 'Ala ka na', french: 'Dieu merci' },
      { bambara: 'dimi', french: 'être malade' },
      { bambara: 'baara', french: 'travail' },
      { bambara: 'so', french: 'maison' },
      { bambara: 'i ni ce', french: 'merci' },
      { bambara: 'Ala bɛ a dɛmɛ', french: 'Dieu l\'aidera' },
      { bambara: 'taa ni hɛrɛ ye', french: 'pars en paix' },
      { bambara: 'to kɛnɛ', french: 'reste en bonne santé' },
    ],
    steps: [
      {
        id: 's1',
        message: 'I ni sogoma, Bakary !',
        translation: 'Bonjour Bakary !',
        options: [
          { id: 'a', text: 'I ni sogoma, Moussa ! I ka kɛnɛ wa ?', translation: 'Bonjour Moussa ! Tu vas bien ?', isCorrect: true },
          { id: 'b', text: 'Ne bɛ sɔn.', translation: 'Je suis d\'accord.', isCorrect: false },
          { id: 'c', text: 'Baara bɛ kɔnɔ.', translation: 'J\'ai du travail.', isCorrect: false },
        ],
        correctReaction: 'Très bien ! On se salue toujours chaleureusement en bambara.',
      },
      {
        id: 's2',
        message: 'Tɔrɔ tɛ, ce ! I ka kɛnɛ fana wa ?',
        translation: 'Pas de problème, ami ! Et toi tu vas bien ?',
        options: [
          { id: 'a', text: 'Ne ka kɛnɛ, i ni ce. I ka somɔgɔw ka kɛnɛ wa ?', translation: 'Je vais bien, merci. Ta famille va bien ?', isCorrect: true },
          { id: 'b', text: 'Ne bɛ taa sisan.', translation: 'Je pars maintenant.', isCorrect: false },
          { id: 'c', text: 'So ka jan.', translation: 'La maison est loin.', isCorrect: false },
        ],
        correctReaction: 'Bien joué ! Demander des nouvelles de la famille est essentiel en bambara.',
        hint: '"ce" = ami (terme d\'affection entre amis)',
      },
      {
        id: 's3',
        message: 'Ayi tɔrɔ tɛ, u bɛɛ ka kɛnɛ. I fa ka kɛnɛ wa ?',
        translation: 'Ils vont tous bien. Ton père va bien ?',
        options: [
          { id: 'a', text: 'O fana tɔrɔ tɛ. I ba ka kɛnɛ wa ?', translation: 'Lui aussi ça va. Ta mère va bien ?', isCorrect: true },
          { id: 'b', text: 'Ne tɛ a dɔn.', translation: 'Je ne le connais pas.', isCorrect: false },
          { id: 'c', text: 'Ne bɛ don.', translation: 'J\'entre.', isCorrect: false },
        ],
        correctReaction: 'Excellent ! "O fana tɔrɔ tɛ" = lui aussi ça va. Très utile !',
        hint: '"fa" = père / "ba" = mère',
      },
      {
        id: 's4',
        message: 'A ka kɛnɛ, Ala ka na. I cɛkɔrɔba ka kɛnɛ wa ?',
        translation: 'Elle va bien, Dieu merci. Ton grand-père va bien ?',
        options: [
          { id: 'a', text: 'A ka kɛnɛ, Ala ka na. I musokɔrɔba ka kɛnɛ wa ?', translation: 'Il va bien, Dieu merci. Ta grand-mère va bien ?', isCorrect: true },
          { id: 'b', text: 'Ne tɛ a fe.', translation: 'Je ne le veux pas.', isCorrect: false },
          { id: 'c', text: 'Sini ne bɛ taa.', translation: 'Demain je pars.', isCorrect: false },
        ],
        correctReaction: 'Bravo ! "Ala ka na" (Dieu merci) est très courant dans les salutations.',
        hint: '"cɛkɔrɔba" = grand-père / "musokɔrɔba" = grand-mère',
      },
      {
        id: 's5',
        message: 'A ka kɛnɛ fana, i ni ce. I deenw ka kɛnɛ wa ?',
        translation: 'Elle va bien aussi, merci. Tes enfants vont bien ?',
        options: [
          { id: 'a', text: 'U ka kɛnɛ, Ala ka na. I muso ka kɛnɛ wa ?', translation: 'Ils vont bien, Dieu merci. Ta femme va bien ?', isCorrect: true },
          { id: 'b', text: 'Deen tɛ yan.', translation: 'L\'enfant n\'est pas là.', isCorrect: false },
          { id: 'c', text: 'Ne tɛ deen sɔrɔ.', translation: 'Je n\'ai pas d\'enfant.', isCorrect: false },
        ],
        correctReaction: 'Super ! "U" = ils/elles. Le bambara distingue clairement singulier et pluriel.',
        hint: '"deenw" = les enfants / "muso" = femme/épouse',
      },
      {
        id: 's6',
        message: 'A tɛ dɔ, a ka dimi dɔɔni. Nga Ala bɛ a dɛmɛ.',
        translation: 'Elle est un peu malade. Mais Dieu l\'aidera.',
        options: [
          { id: 'a', text: 'Ayi tɔrɔ tɛ, Ala ka a kɛnɛya. I ka baara ka kɛnɛ wa ?', translation: 'Que Dieu le guérisse. Ton travail se passe bien ?', isCorrect: true },
          { id: 'b', text: 'A ka taa so.', translation: 'Qu\'elle rentre à la maison.', isCorrect: false },
          { id: 'c', text: 'Ne tɛ a faamu.', translation: 'Je ne comprends pas.', isCorrect: false },
        ],
        correctReaction: 'Très bien ! "Ala ka a kɛnɛya" = que Dieu le guérisse. Belle formule.',
        hint: '"dimi" = être malade / "kɛnɛya" = guérir',
      },
      {
        id: 's7',
        message: 'Baara tɛ ɲi kosɛbɛ sisan, wari dɔgɔ. Nga ne tɛ tɔrɔ.',
        translation: 'Le travail ne va pas très bien, l\'argent manque. Mais je ne me plains pas.',
        options: [
          { id: 'a', text: 'Ne faamu. Ala bɛ i dɛmɛ, tɔrɔ tɛ. I ka so ka kɛnɛ wa ?', translation: 'Je comprends. Dieu t\'aidera. Ta maison va bien ?', isCorrect: true },
          { id: 'b', text: 'I ka baara sɔrɔ dɔɔni.', translation: 'Tu trouveras du travail bientôt.', isCorrect: false },
          { id: 'c', text: 'Dumuni bɛ wa ?', translation: 'Y a-t-il à manger ?', isCorrect: false },
        ],
        correctReaction: 'Parfait ! "Ne faamu" = je comprends. Montrer de l\'empathie est important.',
        hint: '"wari" = argent / "faamu" = comprendre',
      },
      {
        id: 's8',
        message: 'So tɔrɔ tɛ, i ni ce. Aw bɛɛ ka kɛnɛ wa, i ka dɔgɔkɔw fana ?',
        translation: 'La maison ça va. Vos cadets vont bien aussi ?',
        options: [
          { id: 'a', text: 'U ka kɛnɛ, Ala ka na. Ne bɛ u fo i ye.', translation: 'Ils vont bien, Dieu merci. Je te les salue.', isCorrect: true },
          { id: 'b', text: 'U tɛ dɔgɔ.', translation: 'Ils ne sont pas cadets.', isCorrect: false },
          { id: 'c', text: 'Ne bɛ taa sisan.', translation: 'Je pars maintenant.', isCorrect: false },
        ],
        correctReaction: 'Excellent ! "Ne bɛ u fo i ye" = je te les salue. Belle formule de politesse.',
        hint: '"dɔgɔkɔw" = cadets / "fo" = saluer',
      },
      {
        id: 's9',
        message: 'I ni ce, ne fana bɛ ne ka somɔgɔw fo i ye. Ayiwa, ne bɛ taa sisan, baara bɛ kɔnɔ.',
        translation: 'Merci, je salue ma famille de ta part. Bon, je pars, j\'ai du travail.',
        options: [
          { id: 'a', text: 'Ayiwa, Ala k\'i to kɛnɛ ! I ka taa ni hɛrɛ ye !', translation: 'D\'accord, que Dieu te garde ! Pars en paix !', isCorrect: true },
          { id: 'b', text: 'Ne bɛ don sisan.', translation: 'J\'entre maintenant.', isCorrect: false },
          { id: 'c', text: 'So ka jan wa ?', translation: 'La maison est loin ?', isCorrect: false },
        ],
        correctReaction: 'Super ! "I ka taa ni hɛrɛ ye" = pars en paix. Formule d\'au revoir chaleureuse.',
        hint: '"hɛrɛ" = paix / "kɔnɔ" = beaucoup / à l\'intérieur',
      },
      {
        id: 's10',
        message: 'Ala k\'i to kɛnɛ fana, Bakary ! I ka to ni hɛrɛ ye !',
        translation: 'Que Dieu te garde aussi Bakary ! Reste en paix !',
        options: [
          { id: 'a', text: 'I ni ce, Moussa ! Ala k\'an to kɛnɛ bɛɛ !', translation: 'Merci Moussa ! Que Dieu nous garde tous !', isCorrect: true },
          { id: 'b', text: 'Ne bɛ taa fana sisan.', translation: 'Je pars aussi maintenant.', isCorrect: false },
          { id: 'c', text: 'Baara ka gɛlɛn today.', translation: 'Le travail est difficile today.', isCorrect: false },
        ],
        correctReaction: '🎉 Félicitations ! Tu maîtrises les salutations bambara entre amis !',
      },
    ],
  },

  // ── Se présenter ─────────────────────────────────────────────────────────
  //
  // Sources vérifiées :
  //   • CORRECTION s1 : "I tɔgɔ ye mun ?" → "I tɔgɔ ye di ?" — Wikipedia bambara ✅
  //   • "N tɔgɔ ye [prénom] ye" = je m'appelle — Wikipedia bambara ✅
  //   • "I bé bɔ min ?" = tu viens d'où ? — Ankataa ✅
  //   • "N bɔ Faransi" = je viens de France — Ankataa ✅
  //   • "I bé bamanankan fɔ wa ?" = tu parles bambara ? — structure standard ✅
  //   • "San jɔli bé i la ?" = quel âge as-tu ? — Peace Corps Mali Manual ✅
  //   • "Ne ye san mugan ni duuru ye" = j'ai 25 ans — Peace Corps Mali Manual ✅
  //   • "I ka di ne ye" = ravi de te voir — Wikibooks Bambara/Leçon 1 ✅
  

  // ── Au restaurant ──────────────────────────────────────────────────────────
  //
  // Sources vérifiées :
  //   • "N ka kɛnɛ" = je vais bien — Wikibooks ✅
  //   • "N bé dumuni kɛ" = je veux manger — structure "bé + verbe" Auberbabel ✅
  //   • "N bé jɛgɛ ni kini fɛ" = je veux poisson et riz — Ankataa ✅
  //   • "ji fɛ" = vouloir de l'eau — Ankataa (N bɛ ji fɛ) ✅
  //   • CORRECTION s1 : message condensait 3 questions + correctReaction répétait.
  //     Restructuré : s1 = accueil, s2 = menu, flot naturel.
  //   • "wari kɛmɛ saba" = 300 CFA pour un repas — Petit Futé Mali : repas entre
  //     500 et 1000 CFA. Corrigé en "wari kɛmɛ fila" (1000 CFA) plus réaliste ✅
  //   • "I ni baara" (sing.) ≠ "Aw ni baara" (plur.) — Ankataa ✅


  // ── Dans un taxi ───────────────────────────────────────────────────────────
  //
  // Sources vérifiées :
  //   • "I bé taa min ?" = tu vas où ? — Ankataa ("I bɛ bɔ min?" = même structure) ✅
  //   • CORRECTION majeure kɛmɛ :
  //     kɛmɛ = 500 CFA en usage monétaire malien (wikivoyage Mali, Guide linguistique)
  //     "wari kɛmɛ tan" = 10 × 500 = 5000 CFA (PAS 1000 CFA comme indiqué à tort)
  //     "wari kɛmɛ segin" = 8 × 500 = 4000 CFA
  //     Corrigé en "wari kɛmɛ fila" (1000 CFA) + "wari kɛmɛ kelen" (500 CFA)
  //     — prix réalistes pour un taxi urbain Bamako (500-6000 CFA, willridenour.com) ✅
  //   • "N bé taa n tɛrɛ fɛ" = je vais voir mon ami — structure "bé taa [pers] fɛ" ✅
  //   • "An sera" = on est arrivé — "sera" = accompli de "se" (atteindre) ✅
  //   • "Jɔ yan !" = arrête ici ! — structure impérative ✅



  // ── Au marché : fruits & légumes ─────────────────────────────────────────
  {
    id: 'sugu-jiri-ba',
    title: 'Au marché : fruits & légumes',
    context: '🛒 Tu es Mariam. Tu retrouves Aminata, vendeuse au marché de Bamako. Tu cherches des fruits et des légumes.',
    icon: '🛒',
    difficulty: 'débutant',
    character: { name: 'Aminata', emoji: '👩🏿' },
    xpReward: 60,
    lexicon: [
      { bambara: 'jiri jɛ', french: 'fruits' },
      { bambara: 'jaba', french: 'légumes' },
      { bambara: 'mango', french: 'mangue' },
      { bambara: 'tòmàtì', french: 'tomate' },
      { bambara: 'furu', french: 'gombo' },
      { bambara: 'bànjèn', french: 'aubergine' },
      { bambara: 'kɔnɔ', french: 'poisson' },
      { bambara: 'sì', french: 'beurre de karité' },
      { bambara: 'kɔrɔtɔ', french: 'mûr(e)' },
      { bambara: 'ɲini', french: 'chercher' },
      { bambara: 'feere', french: 'vendre' },
      { bambara: 'san', french: 'acheter' },
      { bambara: 'joli', french: 'combien' },
      { bambara: 'kɛmɛ', french: 'cent (francs)' },
      { bambara: 'bi', french: 'vingt' },
      { bambara: 'bisɔnna', french: 'cinquante' },
      { bambara: 'dɔgɔya', french: 'baisser (le prix)' },
      { bambara: 'jatigi', french: 'client(e) fidèle' },
    ],
    steps: [
      {
        id: 's1',
        message: 'I ni sogoma, Mariam ! I ka kɛnɛ wa ?',
        translation: 'Bonjour Mariam ! Tu vas bien ?',
        options: [
          { id: 'a', text: 'I ni sogoma, Aminata ! Tɔrɔ tɛ, i ni ce. I ka kɛnɛ fana wa ?', translation: 'Bonjour Aminata ! Pas de problème, merci. Et toi tu vas bien ?', isCorrect: true },
          { id: 'b', text: 'Ne bɛ kɔnɔ ɲini.', translation: 'Je cherche du poisson.', isCorrect: false },
          { id: 'c', text: 'Sugu ka jan kosɛbɛ.', translation: 'Le marché est très loin.', isCorrect: false },
        ],
        correctReaction: 'Très bien ! On commence toujours par se saluer chaleureusement.',
      },
      {
        id: 's2',
        message: 'Ne ka kɛnɛ, Ala ka na. I bɛ min ɲini yɔrɔ nin na ?',
        translation: 'Je vais bien, Dieu merci. Qu\'est-ce que tu cherches ici ?',
        options: [
          { id: 'a', text: 'Ne bɛ jiri jɛ ni jaba ɲini.', translation: 'Je cherche des fruits et des légumes.', isCorrect: true },
          { id: 'b', text: 'Ne bɛ dɔkɔtɔrɔso taa.', translation: 'Je vais à l\'hôpital.', isCorrect: false },
          { id: 'c', text: 'Ne bɛ sɔgɔ feere.', translation: 'Je vends de la viande.', isCorrect: false },
        ],
        correctReaction: 'Parfait ! "ɲini" = chercher. Tu sais exprimer ce que tu cherches !',
        hint: '"ɲini" = chercher / "jiri jɛ" = fruits / "jaba" = légumes',
      },
      {
        id: 's3',
        message: 'Ayi, jiri jɛ bɛ yan kosɛbɛ ! Mango, banane, orange fana bɛ yan. I bɛ jiri jɛ jumɛn fe ?',
        translation: 'Oui, il y a beaucoup de fruits ! Mangues, bananes, oranges. Quel fruit tu veux ?',
        options: [
          { id: 'a', text: 'Ne bɛ mango fe kosɛbɛ. Mango kɔrɔtɔ bɛ joli ?', translation: 'Je veux surtout des mangues. Les mangues mûres coûtent combien ?', isCorrect: true },
          { id: 'b', text: 'Ne tɛ jiri jɛ fe, ne bɛ kɔnɔ fe.', translation: 'Je ne veux pas de fruits, je veux du poisson.', isCorrect: false },
          { id: 'c', text: 'Ne bɛ taa so sisan.', translation: 'Je rentre à la maison maintenant.', isCorrect: false },
        ],
        correctReaction: 'Bravo ! "kɔrɔtɔ" = mûr(e). Tu sais demander le prix !',
        hint: '"jumɛn" = lequel/quel / "kɔrɔtɔ" = mûr / "joli" = combien',
      },
      {
        id: 's4',
        message: 'Mango kɔrɔtɔ tan bɛ wari kɛmɛ fila.',
        translation: 'Dix mangues mûres coûtent deux cents francs.',
        options: [
          { id: 'a', text: 'Wari kɛmɛ fila ? O ka gèlen ! I bɛ se ka dɔgɔya wa ?', translation: 'Deux cents francs ? C\'est cher ! Tu peux baisser le prix ?', isCorrect: true },
          { id: 'b', text: 'Mango ka dɔgɔ, ne tɛ mango fe.', translation: 'Les mangues sont petites, je ne veux pas de mangues.', isCorrect: false },
          { id: 'c', text: 'Wari bɛ suguya na.', translation: 'L\'argent est dans le sac.', isCorrect: false },
        ],
        correctReaction: 'Excellent ! "gèlen" = cher / "dɔgɔya" = baisser. La négociation commence !',
        hint: '"gèlen" = cher / "dɔgɔya" = baisser le prix',
      },
      {
        id: 's5',
        message: 'Ayiwa, i ye ne ka jatigi ye. Ne bɛ se ka wari kɛmɛ kelen ni bisɔnna kɛ i ye.',
        translation: 'D\'accord, tu es ma fidèle cliente. Je peux faire cent cinquante pour toi.',
        options: [
          { id: 'a', text: 'Aw ni ce ! Ayiwa, ne bɛ mango tan san. Jaba fana bɛ ye wa ?', translation: 'Merci beaucoup ! D\'accord, j\'achète dix mangues. Tu as aussi des légumes ?', isCorrect: true },
          { id: 'b', text: 'Ne tɛ i dɔn, ne tɛ i jatigi ye.', translation: 'Je ne te connais pas, je ne suis pas ta cliente.', isCorrect: false },
          { id: 'c', text: 'Wari kɛmɛ kelen ka gɛlɛn fana.', translation: 'Cent francs aussi c\'est trop difficile.', isCorrect: false },
        ],
        correctReaction: 'Super ! "jatigi" = client(e) fidèle. "bisɔnna" = cinquante. Tu négocies bien !',
        hint: '"jatigi" = client(e) fidèle / "bisɔnna" = cinquante / "san" = acheter',
      },
      {
        id: 's6',
        message: 'Ayi, jaba bɛ yan fana. Tòmàtì, bànjèn, furu fana bɛ yan. I bɛ jumɛn fe ?',
        translation: 'Oui, il y a aussi des légumes. Tomates, aubergines, gombo. Tu veux quoi ?',
        options: [
          { id: 'a', text: 'Ne bɛ tòmàtì ni furu fe. U bɛ joli ?', translation: 'Je veux des tomates et du gombo. Ils coûtent combien ?', isCorrect: true },
          { id: 'b', text: 'Jaba tɛ duman, ne tɛ jaba dun.', translation: 'Les légumes ne sont pas bons, je ne mange pas de légumes.', isCorrect: false },
          { id: 'c', text: 'Ne bɛ banane san sisan.', translation: 'J\'achète des bananes maintenant.', isCorrect: false },
        ],
        correctReaction: 'Parfait ! "tòmàtì" = tomate / "furu" = gombo. Tu connais les légumes !',
        hint: '"tòmàtì" = tomate / "bànjèn" = aubergine / "furu" = gombo',
      },
      {
        id: 's7',
        message: 'Tòmàtì kɛmɛ ye wari bi ye. Furu fana bɛ wari bi ye.',
        translation: 'Un tas de tomates coûte vingt francs. Le gombo aussi coûte vingt francs.',
        options: [
          { id: 'a', text: 'Ayiwa, ne bɛ u bɛɛ san. Kɔnɔ fana bɛ ye wa ?', translation: 'D\'accord, je les achète tous. Tu as du poisson aussi ?', isCorrect: true },
          { id: 'b', text: 'Ne tɛ wari sɔrɔ, ne tɛ se ka san.', translation: 'Je n\'ai pas d\'argent, je ne peux pas acheter.', isCorrect: false },
          { id: 'c', text: 'Tòmàtì ka janya, ne tɛ u fe.', translation: 'Les tomates sont trop grandes, je n\'en veux pas.', isCorrect: false },
        ],
        correctReaction: 'Bien joué ! "bi" = vingt. Tu sais acheter plusieurs choses à la fois !',
        hint: '"bi" = vingt / "u bɛɛ" = tous / "san" = acheter',
      },
      {
        id: 's8',
        message: 'Ayi, kɔnɔ tɛ ne fɛ. Nga kɔnɔjɛ bɛ Fanta fɛ, a bɛ kɔfɛ dɔɔni.',
        translation: 'Non, je n\'ai pas de poisson. Mais Fanta en a, elle est juste derrière.',
        options: [
          { id: 'a', text: 'Aw ni ce ! Sì bɛ ye fana wa ? Ne bɛ sì ɲini fana.', translation: 'Merci ! Tu as aussi du beurre de karité ? Je cherche aussi du beurre.', isCorrect: true },
          { id: 'b', text: 'Fanta ye jɔn ye ? Ne m\'a dɔn.', translation: 'Qui est Fanta ? Je ne la connais pas.', isCorrect: false },
          { id: 'c', text: 'Kɔnɔ tɛ duman, ne tɛ kɔnɔ fe.', translation: 'Le poisson n\'est pas bon, je ne veux pas de poisson.', isCorrect: false },
        ],
        correctReaction: 'Très bien ! "sì" = beurre de karité. Tu continues tes achats !',
        hint: '"kɔnɔjɛ" = poissonnier(e) / "kɔfɛ" = derrière / "sì" = beurre de karité',
      },
      {
        id: 's9',
        message: 'Ayi, sì tɛ ne fɛ. Nga Korotimi bɛ a feere kɛ ɲɔgɔn na, a bɛ sugu kɔnɔ.',
        translation: 'Non, je n\'ai pas de karité. Mais Korotimi en vend à côté, elle est dans le marché.',
        options: [
          { id: 'a', text: 'Ayiwa. Fɛn bɛɛ bɛ joli sisan ?', translation: 'D\'accord. Tout ça coûte combien en tout ?', isCorrect: true },
          { id: 'b', text: 'Korotimi bɛ min feere ?', translation: 'Qu\'est-ce que Korotimi vend ?', isCorrect: false },
          { id: 'c', text: 'Sugu kɔnɔ bɛ jo ?', translation: 'Le marché est où ?', isCorrect: false },
        ],
        correctReaction: 'Parfait ! "fɛn bɛɛ" = tout / "joli" = combien. Tu demandes le total !',
        hint: '"ɲɔgɔn na" = à côté / "kɔnɔ" ici = à l\'intérieur / "fɛn bɛɛ" = tout',
      },
      {
        id: 's10',
        message: 'Mango tan, tòmàtì ni furu : wari kɛmɛ saba.',
        translation: 'Dix mangues, tomates et gombo : trois cents francs au total.',
        options: [
          { id: 'a', text: 'Wari kɛmɛ saba ? I bɛ se ka wari kɛmɛ fila ni bisɔnna kɛ i ye wa ? I ye ne ka jatigi ye !', translation: 'Trois cents francs ? Tu peux faire deux cent cinquante ? Tu m\'as dit que j\'étais ta fidèle cliente !', isCorrect: true },
          { id: 'b', text: 'Ayiwa, ne bɛ wari kɛmɛ naani don.', translation: 'D\'accord, je donne quatre cents francs.', isCorrect: false },
          { id: 'c', text: 'Ne tɛ fɛn san, ne bɛ taa sisan.', translation: 'Je n\'achète rien, je pars maintenant.', isCorrect: false },
        ],
        correctReaction: 'Excellent ! Tu utilises tes arguments pour négocier. "jatigi" = atout de taille !',
        hint: '"kɛmɛ saba" = 300 / "kɛmɛ fila ni bisɔnna" = 250 / "jatigi" = cliente fidèle',
      },
      {
        id: 's11',
        message: 'Ayiwa, i ye ne tùn ! Wari kɛmɛ fila ni bisɔnna, ayiwa !',
        translation: 'D\'accord, tu as gagné ! Deux cent cinquante, c\'est bon !',
        options: [
          { id: 'a', text: 'Aw ni ce, Aminata ! I ka fɛn bɛɛ ka di kosɛbɛ !', translation: 'Merci beaucoup Aminata ! Toutes tes choses sont très bonnes !', isCorrect: true },
          { id: 'b', text: 'Ne tɛ sɔn, ne bɛ wari kɛmɛ kelen fɛ.', translation: 'Je ne suis pas d\'accord, je veux cent francs.', isCorrect: false },
          { id: 'c', text: 'I ka fɛn bɛɛ ka janya kosɛbɛ.', translation: 'Toutes tes choses sont très grandes.', isCorrect: false },
        ],
        correctReaction: 'Bravo ! "tùn" = avoir raison / gagner. Le compliment final est parfait !',
        hint: '"tùn" = avoir raison / "di" = bon/agréable',
      },
      {
        id: 's12',
        message: 'I ni ce fana, Mariam ! Kɔsɛgɛn tuma bɛɛ !',
        translation: 'Merci aussi Mariam ! Reviens à tout moment !',
        options: [
          { id: 'a', text: 'Ayi tɔrɔ tɛ ! Ala k\'i to kɛnɛ ! I ka to ni hɛrɛ ye !', translation: 'Pas de problème ! Que Dieu te garde ! Reste en paix !', isCorrect: true },
          { id: 'b', text: 'Ne tɛ kɔsɛgɛn, sugu ka gɛlɛn.', translation: 'Je ne reviendrai pas, le marché est difficile.', isCorrect: false },
          { id: 'c', text: 'Mango tɛ duman tuma bɛɛ.', translation: 'Les mangues ne sont pas toujours bonnes.', isCorrect: false },
        ],
        correctReaction: '🎉 Félicitations ! Tu as fait tes courses au marché de Bamako en bambara !',
      },
    ],
  },

  // ── Dans un taxi : Seydou & Dramane ──────────────────────────────────────
  {
    id: 'taxi-dramane-ba',
    title: 'Dans un taxi avec Seydou',
    context: '🚕 Tu es Dramane. Tu prends un taxi avec Seydou à Bamako pour aller au vieux marché. Tu es pressé pour un entretien d\'embauche.',
    icon: '🚕',
    difficulty: 'débutant',
    character: { name: 'Seydou', emoji: '🧑🏿‍✈️' },
    xpReward: 60,
    lexicon: [
      { bambara: 'marchɛ kɔrɔ', french: 'vieux marché' },
      { bambara: 'lɔgɔ', french: 'embouteillage' },
      { bambara: 'sebenni', french: 'rendez-vous' },
      { bambara: 'waati', french: 'heure / temps' },
      { bambara: 'tile kelen ni kɔnɔntɔn', french: 'treize heures (13h)' },
      { bambara: 'tan ni fila', french: 'douze (midi)' },
      { bambara: 'tuma la', french: 'être pressé' },
      { bambara: 'gwɛlɛya', french: 'faire vite / se dépêcher' },
      { bambara: 'sira dɔgɔman', french: 'raccourci' },
      { bambara: 'tiɲɛ', french: 'vrai / vérité' },
      { bambara: 'lakɔlibaara', french: 'entretien d\'embauche' },
      { bambara: 'dɛsɛ', french: 'espoir / espérer' },
      { bambara: 'karbu', french: 'essence / carburant' },
      { bambara: 'dɛmɛ', french: 'aider' },
    ],
    steps: [
      {
        id: 's1',
        message: 'I ni tile, cɛ ! I bɛ taa min ?',
        translation: 'Bonjour monsieur ! Tu vas où ?',
        options: [
          { id: 'a', text: 'I ni tile, Seydou ! Ne bɛ taa marchɛ kɔrɔ la.', translation: 'Bonjour Seydou ! Je vais au vieux marché.', isCorrect: true },
          { id: 'b', text: 'Ne bɛ taxi feere.', translation: 'Je vends des taxis.', isCorrect: false },
          { id: 'c', text: 'Ne tɛ wari sɔrɔ sisan.', translation: 'Je n\'ai pas d\'argent maintenant.', isCorrect: false },
        ],
        correctReaction: 'Bien ! "marchɛ kɔrɔ" = vieux marché. Tu sais donner ta destination !',
        hint: '"tile" = soleil / bonjour (après-midi) / "taa min" = aller où',
      },
      {
        id: 's2',
        message: 'Marchɛ kɔrɔ ? Ayiwa, don ! Ne bɛ i ta.',
        translation: 'Le vieux marché ? D\'accord, monte ! Je t\'emmène.',
        options: [
          { id: 'a', text: 'Aw ni ce ! Nga ne bɛ tuma la dɔɔni, i ka gwɛlɛya wa ?', translation: 'Merci ! Mais je suis un peu pressé, tu peux faire vite ?', isCorrect: true },
          { id: 'b', text: 'Ayi, ne tɛ don, ne bɛ to yan.', translation: 'Non, je ne monte pas, je reste ici.', isCorrect: false },
          { id: 'c', text: 'Marchɛ kɔrɔ tɛ ɲi, ne bɛ taa yɔrɔ wɛrɛ.', translation: 'Le vieux marché n\'est pas bien, je vais ailleurs.', isCorrect: false },
        ],
        correctReaction: 'Parfait ! "tuma la" = être pressé. "gwɛlɛya" = faire vite !',
        hint: '"don" = monter / "tuma la" = être pressé / "gwɛlɛya" = faire vite',
      },
      {
        id: 's3',
        message: 'Ne bɛ min kɛ, nga lɔgɔ bɛ kosɛbɛ sisan. Bamako kɔnɔ lɔgɔ tɛ ban tuma si.',
        translation: 'Je ferai ce que je peux, mais beaucoup d\'embouteillages. À Bamako ça ne finit jamais.',
        options: [
          { id: 'a', text: 'Sɔni, o ye tiɲɛ ye. I bɛ sira wɛrɛ lajɛ wa ? Sira jan tɛ ɲi sisan.', translation: 'C\'est vrai. Tu peux chercher une autre route ? La longue route n\'est pas bonne.', isCorrect: true },
          { id: 'b', text: 'Lɔgɔ ka di ne ye, ne tɛ tuma la.', translation: 'J\'aime les embouteillages, je ne suis pas pressé.', isCorrect: false },
          { id: 'c', text: 'Bamako tɛ Bamako ye sisan.', translation: 'Bamako n\'est plus Bamako maintenant.', isCorrect: false },
        ],
        correctReaction: 'Excellent ! "tiɲɛ" = vrai. "lajɛ" = chercher / considérer. Bien !',
        hint: '"lɔgɔ" = embouteillage / "ban" = finir / "tiɲɛ" = vrai / "lajɛ" = chercher',
      },
      {
        id: 's4',
        message: 'Ayi tɔrɔ tɛ. Ne bɛ sira dɔgɔman ta. An bɛ taa Medina fɛ, o ka ɲi.',
        translation: 'Pas de problème. Je prends le raccourci. On passe par Médina, c\'est mieux.',
        options: [
          { id: 'a', text: 'Medina fɛ ? Aw ni ce ! I ye Bamako dɔn kosɛbɛ cɛ !', translation: 'Par Médina ? Merci ! Tu connais vraiment bien Bamako !', isCorrect: true },
          { id: 'b', text: 'Medina tɛ ɲi, an kana taa yen, ne tɛ sɔn.', translation: 'Médina c\'est pas bien, n\'y allons pas, je ne suis pas d\'accord.', isCorrect: false },
          { id: 'c', text: 'Sira dɔgɔman ye mun ye ?', translation: 'C\'est quoi un raccourci ?', isCorrect: false },
        ],
        correctReaction: 'Super ! "sira dɔgɔman" = raccourci. Tu complimentes le chauffeur !',
        hint: '"sira dɔgɔman" = raccourci / "fɛ" ici = par / via / "cɛ" = vraiment',
      },
      {
        id: 's5',
        message: 'Ee, ne bɛ taxi kɛ san tan. Ne ye sira bɛɛ dɔn. Nga i bɛ tuma la, i ka sebenni bɛ waati juman na ?',
        translation: 'Oui, je fais le taxi depuis dix ans. Je connais toutes les routes. Ton rendez-vous est à quelle heure ?',
        options: [
          { id: 'a', text: 'A bɛ tile kelen ni kɔnɔntɔn na. Sisan waati juman ye ?', translation: 'Il est à treize heures. Il est quelle heure maintenant ?', isCorrect: true },
          { id: 'b', text: 'Ne tɛ sebenni sɔrɔ, ne bɛ taa lɔ.', translation: 'Je n\'ai pas de rendez-vous, je vais me promener.', isCorrect: false },
          { id: 'c', text: 'San tan ye dɔgɔ ye, i tɛ baara dɔn.', translation: 'Dix ans c\'est peu, tu ne connais pas le travail.', isCorrect: false },
        ],
        correctReaction: '"tile kelen ni kɔnɔntɔn" = 13h. "san tan" = dix ans. Tu gères les heures !',
        hint: '"waati juman" = quelle heure / "tile kelen ni kɔnɔntɔn" = treize heures',
      },
      {
        id: 's6',
        message: 'Sisan waati tan ni fila. I ka sebenni bɛ dɔɔni, nga tɔrɔ tɛ. An bɛ se ka se yen ni hɛrɛ ye.',
        translation: 'Il est midi. Ton rendez-vous est bientôt, mais pas de souci. On peut y arriver tranquillement.',
        options: [
          { id: 'a', text: 'Aw ni ce, i ye ne hɛrɛya ! Ne tɛ se ka sebenni bɔ.', translation: 'Merci, tu me rassures ! Je ne peux pas rater ce rendez-vous.', isCorrect: true },
          { id: 'b', text: 'Ayi, an tɛ se ka se yen, lɔgɔ ka bon.', translation: 'Non, on ne peut pas y arriver, l\'embouteillage est grand.', isCorrect: false },
          { id: 'c', text: 'Waati tan ni fila ye juman ye ?', translation: 'Midi c\'est quoi ?', isCorrect: false },
        ],
        correctReaction: '"hɛrɛya" = rassurer. "sebenni bɔ" = rater le rendez-vous. Bien exprimé !',
        hint: '"tan ni fila" = douze (midi) / "hɛrɛya" = rassurer / "bɔ" ici = rater / manquer',
      },
      {
        id: 's7',
        message: 'I ka sebenni bɛ mun na ye ?',
        translation: 'C\'est un rendez-vous pour quoi ?',
        options: [
          { id: 'a', text: 'Ne bɛ baara kura ɲini. Ne ka lakɔlibaara bɛ ne kɔnɔ.', translation: 'Je cherche un nouveau travail. J\'ai un entretien.', isCorrect: true },
          { id: 'b', text: 'Ne tɛ i ka sebenni ɲini, nin tɛ i ka baara ye.', translation: 'Je ne cherche pas ton rendez-vous, ce n\'est pas ton travail.', isCorrect: false },
          { id: 'c', text: 'Sebenni ye mun ye ? Ne m\'a faamu.', translation: 'C\'est quoi un rendez-vous ? Je ne comprends pas.', isCorrect: false },
        ],
        correctReaction: '"baara kura" = nouveau travail. "lakɔlibaara" = entretien. Vocabulaire riche !',
        hint: '"baara kura" = nouveau travail / "lakɔlibaara" = entretien d\'embauche',
      },
      {
        id: 's8',
        message: 'Aw ni ce ! Ala k\'i dɛmɛ ! Baara ka wajibi sisan, wari bɛ gɛlɛn.',
        translation: 'Bravo ! Que Dieu t\'aide ! Le travail est important, l\'argent est difficile.',
        options: [
          { id: 'a', text: 'Sɔni, o ye tiɲɛ ye. Fantan ni dimi lo sisan. Nga ne bɛ dɛsɛ.', translation: 'C\'est vrai. Les pauvres souffrent en ce moment. Mais j\'espère.', isCorrect: true },
          { id: 'b', text: 'Ne tɛ baara ɲini, ne bɛ lɔ.', translation: 'Je ne cherche pas de travail, je me promène.', isCorrect: false },
          { id: 'c', text: 'Wari tɛ gɛlɛn, fɛn bɛɛ ka ɲi.', translation: 'L\'argent n\'est pas difficile, tout va bien.', isCorrect: false },
        ],
        correctReaction: '"fantan" = pauvre. "dɛsɛ" = espoir. Tu parles de la vie avec sagesse !',
        hint: '"wajibi" = important / "fantan" = pauvre / "dɛsɛ" = espoir / espérer',
      },
      {
        id: 's9',
        message: 'Aw ni ce i ka dɛsɛ kɔrɔ ! An ye se yen sisan. Marchɛ kɔrɔ ye nin ye !',
        translation: 'Bravo pour ton espoir ! On est arrivés. Voilà le vieux marché !',
        options: [
          { id: 'a', text: 'Aw ni ce kosɛbɛ Seydou ! I ye gwɛlɛya kosɛbɛ ! Taxi bɛ joli ?', translation: 'Merci beaucoup Seydou ! Tu as vraiment fait vite ! Le taxi coûte combien ?', isCorrect: true },
          { id: 'b', text: 'Nin tɛ marchɛ kɔrɔ ye, i ye sira juguya ta.', translation: 'Ce n\'est pas le vieux marché, tu as pris la mauvaise route.', isCorrect: false },
          { id: 'c', text: 'An tɛ se yen fɔlɔ, an ka to taxi kɔnɔ.', translation: 'On n\'est pas encore arrivés, restons dans le taxi.', isCorrect: false },
        ],
        correctReaction: 'Arrivée ! "gwɛlɛya" = faire vite. Tu félicites Seydou et demandes le prix !',
        hint: '"se yen" = arriver là / "gwɛlɛya" = faire vite / "joli" = combien',
      },
      {
        id: 's10',
        message: 'Wari kɛmɛ fila ni bisɔnna.',
        translation: 'Deux cent cinquante francs.',
        options: [
          { id: 'a', text: 'Wari kɛmɛ fila ni bisɔnna ? O ka gɛlɛn dɔɔni. I bɛ se ka wari kɛmɛ fila kɛ wa ?', translation: 'Deux cent cinquante ? C\'est un peu cher. Tu peux faire deux cents ?', isCorrect: true },
          { id: 'b', text: 'Ayiwa, ne bɛ wari kɛmɛ naani don.', translation: 'D\'accord, je donne quatre cents francs.', isCorrect: false },
          { id: 'c', text: 'Ne tɛ wari dɔn, wari ye mun ye ?', translation: 'Je ne connais pas l\'argent, c\'est quoi l\'argent ?', isCorrect: false },
        ],
        correctReaction: '"gɛlɛn" = cher / difficile. "kɛmɛ fila" = 200. La négociation commence !',
        hint: '"kɛmɛ fila ni bisɔnna" = 250 / "kɛmɛ fila" = 200',
      },
      {
        id: 's11',
        message: 'Ayi cɛ, karbu tɛ ɲi sisan, ne tɛ se. Wari kɛmɛ fila ni bisɔnna ye kelen ye.',
        translation: 'Non monsieur, l\'essence est chère, je ne peux pas. Deux cent cinquante c\'est le prix fixe.',
        options: [
          { id: 'a', text: 'Ayiwa, i bɛ sɔn. Nin ye i ta ye. I ye ne dɛmɛ kosɛbɛ tile nin na !', translation: 'D\'accord, tu as raison. Voilà ton argent. Tu m\'as vraiment aidé aujourd\'hui !', isCorrect: true },
          { id: 'b', text: 'Karbu tɛ gɛlɛn, i bɛ gosi ne.', translation: 'L\'essence n\'est pas chère, tu me trompes.', isCorrect: false },
          { id: 'c', text: 'Ne bɛ taa wari sɔrɔ ka na.', translation: 'Je vais chercher de l\'argent et revenir.', isCorrect: false },
        ],
        correctReaction: '"i bɛ sɔn" = tu as raison. "dɛmɛ" = aider. Tu conclus avec élégance !',
        hint: '"karbu" = essence / "kelen" = fixe / "dɛmɛ" = aider',
      },
      {
        id: 's12',
        message: 'Tɔrɔ tɛ cɛ ! Ala k\'i to kɛnɛ ! I ka sebenni kɛ ni hɛrɛ ye, baara kura sɔrɔ !',
        translation: 'Pas de problème monsieur ! Que Dieu te garde ! Bon rendez-vous, trouve ce nouveau travail !',
        options: [
          { id: 'a', text: 'Ala k\'i to kɛnɛ fana Seydou ! I ka baara kɛ ni hɛrɛ ye ! Kɔsɛgɛn !', translation: 'Que Dieu te garde aussi Seydou ! Bon travail ! À bientôt !', isCorrect: true },
          { id: 'b', text: 'Ne tɛ sebenni taa, ne bɛ kɔsɛgɛn taxi na.', translation: 'Je ne vais pas au rendez-vous, je remonte dans le taxi.', isCorrect: false },
          { id: 'c', text: 'I ka taxi fili, o tɛ baara ɲuman ye.', translation: 'Laisse le taxi, ce n\'est pas un bon travail.', isCorrect: false },
        ],
        correctReaction: '🎉 Félicitations ! Tu as pris un taxi à Bamako en bambara du début à la fin !',
      },
    ],
  },

  // ── À la gare routière : billet pour Sikasso ──────────────────────────────
  {
    id: 'gare-sikasso-ba',
    title: 'À la gare : billet pour Sikasso',
    context: '🚌 Tu es Salif. Tu vas à la gare routière de Bamako pour acheter un billet pour Sikasso.',
    icon: '🚌',
    difficulty: 'intermédiaire',
    character: { name: 'Agent de voyage', emoji: '🧑🏿‍✈️' },
    xpReward: 65,
    lexicon: [
      { bambara: 'sɔtigi', french: 'billet (de transport)' },
      { bambara: 'sotɔ', french: 'car / bus' },
      { bambara: 'gare', french: 'gare routière' },
      { bambara: 'sira', french: 'route / chemin' },
      { bambara: 'kɔgɔsiɲɛ', french: 'matin tôt' },
      { bambara: 'waati six', french: 'six heures' },
      { bambara: 'jaɲɔgɔn juru', french: 'ceinture de sécurité' },
      { bambara: 'guichet', french: 'guichet' },
      { bambara: 'wari dɔgɔya', french: 'réduction (de prix)' },
      { bambara: 'dencɛ', french: 'fils / garçon' },
      { bambara: 'kunnafoni', french: 'information' },
      { bambara: 'hɛrɛ', french: 'paix / tranquillité' },
    ],
    steps: [
      {
        id: 's1',
        message: 'I ni sogoma ! I ka kɛnɛ wa ? Ne bɛ i dɛmɛ wa ?',
        translation: 'Bonjour ! Tu vas bien ? Je peux t\'aider ?',
        options: [
          { id: 'a', text: 'I ni sogoma ! Tɔrɔ tɛ, i ni ce. Ne bɛ Sikasso taa, ne bɛ sɔtigi ɲini.', translation: 'Bonjour ! Pas de problème, merci. Je veux aller à Sikasso, je cherche un billet.', isCorrect: true },
          { id: 'b', text: 'Ne bɛ sotɔ feere, i bɛ san wa ?', translation: 'Je vends des cars, tu veux en acheter ?', isCorrect: false },
          { id: 'c', text: 'Gare tɛ ɲi, ne bɛ taa sisan.', translation: 'La gare n\'est pas bien, je pars maintenant.', isCorrect: false },
        ],
        correctReaction: '"sɔtigi" = billet, "Sikasso taa" = aller à Sikasso. Tu ouvres avec clarté !',
        hint: '"sɔtigi" = billet / "ɲini" = chercher / "taa" = aller',
      },
      {
        id: 's2',
        message: 'Ayiwa ! I bɛ taa tile juman ?',
        translation: 'D\'accord ! Tu pars quel jour ?',
        options: [
          { id: 'a', text: 'Ne bɛ taa sini, tile kɔnɔntɔn. Sɔtigi bɛ ye wa ?', translation: 'Je pars demain, le jeudi. Il y a des places ?', isCorrect: true },
          { id: 'b', text: 'Ne tɛ tile dɔn, tile bɛɛ ka kelen.', translation: 'Je ne connais pas les jours, tous les jours sont pareils.', isCorrect: false },
          { id: 'c', text: 'Ne tɛ Sikasso taa, ne bɛ Bamako to.', translation: 'Je ne vais pas à Sikasso, je reste à Bamako.', isCorrect: false },
        ],
        correctReaction: '"sini" = demain. "tile kɔnɔntɔn" = jeudi. Tu précises et poses une question !',
        hint: '"sini" = demain / "tile kɔnɔntɔn" = jeudi / "sɔtigi bɛ ye" = il y a des places',
      },
      {
        id: 's3',
        message: 'Ayi tɔrɔ tɛ, sɔtigi bɛ. I bɛ taa waati juman ?',
        translation: 'Pas de problème, il y a des places. Tu pars à quelle heure ?',
        options: [
          { id: 'a', text: 'Ne bɛ taa kɔgɔsiɲɛ fɔlɔ fɔlɔ. Sotɔ min bɛ bɔ joona ka ɲi ?', translation: 'Je veux partir tôt le matin. Quel car part le plus tôt ?', isCorrect: true },
          { id: 'b', text: 'Ne bɛ taa su kɔnɔ, tile tɛ ne diya.', translation: 'Je veux partir la nuit, le jour ne me plaît pas.', isCorrect: false },
          { id: 'c', text: 'Waati tɛ wajibi, ne bɛ taa joona ni joona.', translation: 'L\'heure n\'est pas importante, je pars dès que possible.', isCorrect: false },
        ],
        correctReaction: '"kɔgɔsiɲɛ fɔlɔ fɔlɔ" = tôt le matin. Tu cherches le car du matin !',
        hint: '"kɔgɔsiɲɛ" = matin / "fɔlɔ fɔlɔ" = très tôt / "bɔ" = partir',
      },
      {
        id: 's4',
        message: 'Sotɔ fɔlɔ bɛ bɔ waati six la. Waati tan la fana sotɔ bɛ bɔ. I bɛ jumɛn fe ?',
        translation: 'Le premier car part à six heures. Il y en a un autre à dix heures. Tu veux lequel ?',
        options: [
          { id: 'a', text: 'Waati six ka ɲi ne ye. Sikasso bɛ jan, ne bɛ joona se yen bɛɛ.', translation: 'Six heures c\'est bien pour moi. Sikasso est loin, je veux arriver tôt.', isCorrect: true },
          { id: 'b', text: 'Sotɔ fila bɛɛ ka kelen, fara tɛ olu cɛ.', translation: 'Les deux cars sont pareils, il n\'y a pas de différence.', isCorrect: false },
          { id: 'c', text: 'Ne tɛ waati six dɔn, waati six ye mun ye ?', translation: 'Je ne connais pas six heures, c\'est quoi six heures ?', isCorrect: false },
        ],
        correctReaction: '"waati six" = six heures. "se yen" = arriver là-bas. Tu justifies ton choix !',
        hint: '"waati six" = six heures / "jan" = loin / "se yen" = arriver là-bas',
      },
      {
        id: 's5',
        message: 'Faamu. Waati six sotɔ bɛ Bamako bɔ ka taa Sikasso. Sira nin bɛ waati naani ni kɔnɔntɔn kɔnɔ banna.',
        translation: 'Le car de six heures part de Bamako pour Sikasso. Ce trajet dure entre quatre et cinq heures.',
        options: [
          { id: 'a', text: 'Ayiwa. Nga sira bɛ ɲi wa ? Ne m\'a kɛ kɔ kosɛbɛ, ne bɛ sira dɔn ɲɔgɔn ɲini.', translation: 'D\'accord. Mais la route est bonne ? Je ne l\'ai pas souvent fait, je veux savoir.', isCorrect: true },
          { id: 'b', text: 'Waati naani ka jan kosɛbɛ, ne tɛ sɔn.', translation: 'Quatre heures c\'est trop long, je ne suis pas d\'accord.', isCorrect: false },
          { id: 'c', text: 'Sikasso bɛ jan wa ? Ne m\'a lɔn.', translation: 'Sikasso est loin ? Je ne savais pas.', isCorrect: false },
        ],
        correctReaction: '"ne m\'a kɛ kɔ" = je ne l\'ai pas souvent fait. Tu demandes des infos utiles !',
        hint: '"banna" = finir / durer / "sira" = route / "dɔn ɲɔgɔn" = mieux savoir',
      },
      {
        id: 's6',
        message: 'Sira ka ɲi kosɛbɛ sisan. U ye a bɔlɔ kura tile damani ye. I bɛ se ka hɛrɛ taa.',
        translation: 'La route est très bonne maintenant. Ils l\'ont refaite il y a quelques jours. Tu peux partir tranquille.',
        options: [
          { id: 'a', text: 'O ka ɲi kosɛbɛ ! Sɔtigi wari bɛ joli ?', translation: 'C\'est très bien ! Le billet coûte combien ?', isCorrect: true },
          { id: 'b', text: 'Sira ka ɲi ? Ne tɛ sɔn, sira bɛ gɛlɛn tuma bɛɛ.', translation: 'La route est bonne ? Je ne suis pas d\'accord, la route est toujours difficile.', isCorrect: false },
          { id: 'c', text: 'Ne tɛ sotɔ fe, ne bɛ wotoro wɛrɛ ɲini.', translation: 'Je ne veux pas de car, je cherche une autre voiture.', isCorrect: false },
        ],
        correctReaction: '"sɔtigi wari bɛ joli" = le billet coûte combien ? Tu passes au prix !',
        hint: '"bɔlɔ kura" = refaire / rénover / "hɛrɛ" = tranquillité / "joli" = combien',
      },
      {
        id: 's7',
        message: 'Sɔtigi kelen bɛ wari kɛmɛ du ni bisɔnna ye. O kɔnɔ, i bɛ sɔtigi sɔrɔ ni dumuni ye.',
        translation: 'Un billet coûte mille cinq cents francs. Avec ça, tu as le billet et un repas inclus.',
        options: [
          { id: 'a', text: 'Dumuni fana bɛ o kɔnɔ ? O ka ɲi ! Nga ne bɛ se ka wari dɔgɔya ɲini wa ? Ne bɛ taa ni ne dencɛ ye.', translation: 'Un repas inclus aussi ? C\'est bien ! Mais je peux demander une réduction ? Je voyage avec mon fils.', isCorrect: true },
          { id: 'b', text: 'Ne tɛ dumuni fe sotɔ kɔnɔ, ne tɛ dun sira kan.', translation: 'Je ne veux pas manger dans le car, je ne mange pas en route.', isCorrect: false },
          { id: 'c', text: 'Wari kɛmɛ du ni bisɔnna ka dɔgɔ, ne bɛ wari fitini san.', translation: 'Mille cinq cents c\'est peu, j\'achète un billet pas cher.', isCorrect: false },
        ],
        correctReaction: '"wari dɔgɔya" = réduction. "dencɛ" = fils. Tu négocies habilement !',
        hint: '"dumuni" = repas / "wari dɔgɔya" = réduction / "dencɛ" = fils',
      },
      {
        id: 's8',
        message: 'I ka dencɛ san joli ye ? Deen dɔgɔ bɛ taa ni sɔtigi dɔgɔman ye.',
        translation: 'Ton fils a quel âge ? Les petits enfants ont un billet réduit.',
        options: [
          { id: 'a', text: 'A bɛ san kɔnɔntɔn. A ka dɔgɔ wa dɔgɔmalen ye wa ?', translation: 'Il a neuf ans. C\'est encore considéré comme petit ?', isCorrect: true },
          { id: 'b', text: 'Ne tɛ dencɛ sɔrɔ, ne bɛ kelen taa.', translation: 'Je n\'ai pas de fils, je voyage seul.', isCorrect: false },
          { id: 'c', text: 'Ne dencɛ ka bon, a bɛ ne kɔ taa.', translation: 'Mon fils est grand, il voyage derrière moi.', isCorrect: false },
        ],
        correctReaction: '"san kɔnɔntɔn" = neuf ans. "dɔgɔmalen" = encore petit. Bonne question !',
        hint: '"san joli" = quel âge / "dɔgɔman" = réduit / "dɔgɔmalen" = encore petit',
      },
      {
        id: 's9',
        message: 'San kɔnɔntɔn, a ka dɔgɔ wa. A sɔtigi bɛ wari kɛmɛ du ye, dumuni tɛ o kɔnɔ.',
        translation: 'Neuf ans, c\'est encore petit. Son billet est à mille francs, sans repas inclus.',
        options: [
          { id: 'a', text: 'Ayiwa. Sɔtigi fila, ne ta ni ne dencɛ ta. Wari bɛɛ bɛ joli ?', translation: 'D\'accord. Deux billets, le mien et celui de mon fils. Combien en tout ?', isCorrect: true },
          { id: 'b', text: 'San kɔnɔntɔn ka bon, a sɔtigi ka kan ka kelen ye ne ta ye.', translation: 'Neuf ans c\'est grand, son billet doit être le même que le mien.', isCorrect: false },
          { id: 'c', text: 'Ne dencɛ tɛ dumuni fe, o tɛ gɛlɛn.', translation: 'Mon fils ne veut pas de repas, ce n\'est pas grave.', isCorrect: false },
        ],
        correctReaction: '"sɔtigi fila" = deux billets. "wari bɛɛ bɛ joli" = combien en tout ? Parfait !',
        hint: '"sɔtigi fila" = deux billets / "ne ta" = le mien / "wari bɛɛ" = en tout',
      },
      {
        id: 's10',
        message: 'I ta bɛ wari kɛmɛ du ni bisɔnna, i dencɛ ta bɛ wari kɛmɛ du. Fɔlɔ wari kɛmɛ mugan ni bisɔnna.',
        translation: 'Le tien à mille cinq cents, celui de ton fils à mille. Au total deux mille cinq cents francs.',
        options: [
          { id: 'a', text: 'Ayiwa, nin ye wari ye. I bɛ se ka sɔtigi sɛbɛn ne ye sisan wa ?', translation: 'D\'accord, voilà l\'argent. Tu peux me faire les billets maintenant ?', isCorrect: true },
          { id: 'b', text: 'Wari kɛmɛ mugan ni bisɔnna ka bon kosɛbɛ, ne tɛ se ka o don.', translation: 'Deux mille cinq cents c\'est beaucoup trop, je ne peux pas payer ça.', isCorrect: false },
          { id: 'c', text: 'Ne bɛ sɔtigi saba ɲini, mɔgɔ wɛrɛ bɛ ne fɛ fana.', translation: 'Je veux trois billets, il y a quelqu\'un d\'autre avec moi.', isCorrect: false },
        ],
        correctReaction: '"nin ye wari ye" = voilà l\'argent. "sɛbɛn" = émettre / faire. Tu règles !',
        hint: '"kɛmɛ mugan ni bisɔnna" = deux mille cinq cents / "sɔtigi sɛbɛn" = faire les billets',
      },
      {
        id: 's11',
        message: 'Ayi tɔrɔ tɛ ! I tɔgɔ ni i ka carte sɛbɛn don wa ?',
        translation: 'Pas de problème ! Ton nom et ta carte d\'identité s\'il te plaît ?',
        options: [
          { id: 'a', text: 'Ne tɔgɔ ye Salif Coulibaly ye. Ne ka carte bɛ nin ye.', translation: 'Mon nom est Salif Coulibaly. Voici ma carte.', isCorrect: true },
          { id: 'b', text: 'Ne tɔgɔ tɛ wajibi, sɔtigi labɛn.', translation: 'Mon nom n\'est pas important, fais juste le billet.', isCorrect: false },
          { id: 'c', text: 'Ne tɛ carte sɔrɔ, ne tɔgɔ tɛ.', translation: 'Je n\'ai pas de carte, je n\'ai pas de nom.', isCorrect: false },
        ],
        correctReaction: '"ne tɔgɔ ye … ye" = mon nom est… Tu te présentes avec assurance !',
        hint: '"tɔgɔ" = nom / "carte" = carte d\'identité / "labɛn" = préparer',
      },
      {
        id: 's12',
        message: 'Aw ni ce. Sɔtigi labɛnnen don. Kɔnɔ, sotɔ bɛ bɔ Sogoniko gare la. I bɛ se ka don waati kelen kɔ.',
        translation: 'Merci. Les billets sont prêts. Attention, le car part de la gare de Sogoniko. Tu dois arriver une heure avant.',
        options: [
          { id: 'a', text: 'Sogoniko gare. Ayiwa, ne bɛ se ka se yen joona. Gare kɔnɔ ne bɛ min lajɛ sotɔ sɔrɔ na ?', translation: 'La gare de Sogoniko. D\'accord. À la gare, où est-ce que je me présente pour monter ?', isCorrect: true },
          { id: 'b', text: 'Sogoniko gare bɛ jan kosɛbɛ, ne tɛ se ka se yen.', translation: 'La gare de Sogoniko est trop loin, je ne peux pas y arriver.', isCorrect: false },
          { id: 'c', text: 'Waati kelen kɔ don ka gɛlɛn, ne bɛ taa sini.', translation: 'Arriver une heure avant c\'est difficile, je pars demain.', isCorrect: false },
        ],
        correctReaction: '"se yen joona" = arriver là-bas vite. Tu confirmes et demandes l\'info finale !',
        hint: '"labɛnnen" = prêt / "waati kelen kɔ" = une heure avant / "lajɛ" = se présenter',
      },
      {
        id: 's13',
        message: 'I bɛ taa guichet kɔrɔ, i bɛ sɔtigi don. U bɛ i jira sotɔ la. Jaɲɔgɔn juru ka ɲi fana, sira bɛ jan.',
        translation: 'Tu vas au guichet principal, tu présentes ton billet. Ils t\'indiqueront le car. Attache bien ta ceinture, la route est longue.',
        options: [
          { id: 'a', text: 'Aw ni ce i ka kunnafoni bɛɛ kosɛbɛ ! Ala k\'i to kɛnɛ ! I ka to ni hɛrɛ ye !', translation: 'Merci beaucoup pour toutes ces informations ! Que Dieu te garde ! Reste en paix !', isCorrect: true },
          { id: 'b', text: 'Ne tɛ jaɲɔgɔn juru fe, o tɛ wajibi.', translation: 'Je ne veux pas de ceinture, ce n\'est pas obligatoire.', isCorrect: false },
          { id: 'c', text: 'Guichet bɛ min ? Ne m\'a ye.', translation: 'Le guichet est où ? Je ne l\'ai pas vu.', isCorrect: false },
        ],
        correctReaction: '🎉 Félicitations ! Tu as acheté ton billet pour Sikasso en bambara comme un vrai voyageur !',
      },
    ],
  },

  // ── À la banque ────────────────────────────────────────────────────────────
  {
    id: 'banki-ba',
    title: 'À la banque',
    context: '🏦 Tu es Boubacar. Tu vas à la banque pour ouvrir un compte.',
    icon: '🏦',
    difficulty: 'intermédiaire',
    character: { name: 'Agent bancaire', emoji: '👩🏿‍💼' },
    xpReward: 55,
    lexicon: [
      { bambara: 'jaabi', french: 'compte (bancaire)' },
      { bambara: 'jaabi ladon', french: 'ouvrir un compte' },
      { bambara: 'jaabi dɔgɔman', french: 'compte simple (courant)' },
      { bambara: 'jaabi bon', french: 'compte épargne' },
      { bambara: 'karta', french: 'carte (d\'identité / bancaire)' },
      { bambara: 'foto', french: 'photo' },
      { bambara: 'fotografi', french: 'photographe' },
      { bambara: 'so ɲɛfɛ sɛbɛn', french: 'justificatif de domicile' },
      { bambara: 'so baaraden', french: 'quittance de loyer' },
      { bambara: 'wari kɛmɛ du', french: 'mille francs' },
      { bambara: 'telefɔni nimɔrɔ', french: 'numéro de téléphone' },
      { bambara: 'wajibi', french: 'obligatoire / nécessaire' },
    ],
    steps: [
      {
        id: 's1',
        message: 'I ni sogoma ! I ka kɛnɛ wa ? Ne bɛ i dɛmɛ wa ?',
        translation: 'Bonjour ! Tu vas bien ? Je peux t\'aider ?',
        options: [
          { id: 'a', text: 'I ni sogoma ! Tɔrɔ tɛ, i ni ce. Ne bɛ ne ka jaabi ladon bɛ ɲini.', translation: 'Bonjour ! Pas de problème, merci. Je voudrais ouvrir un compte bancaire.', isCorrect: true },
          { id: 'b', text: 'Ne bɛ wari feere, i bɛ san wa ?', translation: 'Je vends de l\'argent, tu veux en acheter ?', isCorrect: false },
          { id: 'c', text: 'Banki tɛ ɲi, ne bɛ taa.', translation: 'La banque n\'est pas bien, je pars.', isCorrect: false },
        ],
        correctReaction: '"jaabi ladon" = ouvrir un compte. Tu entames la conversation avec politesse !',
        hint: '"jaabi" = compte / "ladon" = ouvrir / "dɛmɛ" = aider',
      },
      {
        id: 's2',
        message: 'Ayiwa, o ka ɲi ! I ye banki kɔnɔ joona don wa ?',
        translation: 'D\'accord, très bien ! C\'est ta première fois à la banque ?',
        options: [
          { id: 'a', text: 'Ayi, ne tɛ banki kɔnɔ joona don. Nga ne tɛ jaabi sɔrɔ fɔlɔ.', translation: 'Non, ce n\'est pas ma première fois. Mais je n\'avais pas encore de compte.', isCorrect: true },
          { id: 'b', text: 'Banki don juman don ?', translation: 'C\'est quoi une banque ?', isCorrect: false },
          { id: 'c', text: 'Ne tɛ jaabi fe, ne bɛ wari mara so kɔnɔ.', translation: 'Je ne veux pas de compte, je garde l\'argent à la maison.', isCorrect: false },
        ],
        correctReaction: '"joona don" = première fois. "ne tɛ … fɔlɔ" = je n\'avais pas encore…',
        hint: '"joona don" = première fois / "fɔlɔ" = avant / encore',
      },
      {
        id: 's3',
        message: 'Faamu. Jaabi surun bɛ wɛrɛ bɛ : jaabi dɔgɔman ni jaabi bon. I bɛ jumɛn fe ?',
        translation: 'Je comprends. Il y a deux types de comptes : le compte simple et le compte épargne. Tu veux lequel ?',
        options: [
          { id: 'a', text: 'Jaabi surun nunu ka fara ɲɔgɔn kan cogo di ? Ne bɛ a faamu ɲɔgɔn.', translation: 'Quelle est la différence ? Je voudrais mieux comprendre.', isCorrect: true },
          { id: 'b', text: 'Jaabi surun bɛɛ ye kelen ye, fara tɛ.', translation: 'Les deux comptes sont pareils, il n\'y a pas de différence.', isCorrect: false },
          { id: 'c', text: 'Ne tɛ jaabi fe, ne bɛ taa sisan.', translation: 'Je ne veux pas de compte, je pars maintenant.', isCorrect: false },
        ],
        correctReaction: '"fara ɲɔgɔn kan" = différence. Tu demandes des explications avant de choisir !',
        hint: '"fara" = différence / "faamu" = comprendre / "ɲɔgɔn" = entre eux',
      },
      {
        id: 's4',
        message: 'Jaabi dɔgɔman bɛ i to ka wari don ani ka bɔ tuma bɛɛ. Jaabi bon bɛ i to ka wari mara ka mun sɔrɔ.',
        translation: 'Le compte simple permet de déposer et retirer à tout moment. Le compte épargne permet d\'économiser et gagner des intérêts.',
        options: [
          { id: 'a', text: 'Ayiwa, ne ye faamu. Ne bɛ jaabi dɔgɔman fe fɔlɔ. Ne bɛ wari don ni bɔ ɲɔgɔn na.', translation: 'D\'accord, j\'ai compris. Je veux d\'abord un compte simple. Je veux pouvoir déposer et retirer facilement.', isCorrect: true },
          { id: 'b', text: 'Ne tɛ faamu, bambara tɛ ne ka kan.', translation: 'Je ne comprends pas, le bambara n\'est pas ma langue.', isCorrect: false },
          { id: 'c', text: 'Jaabi bon ni jaabi dɔgɔman bɛɛ ka gɛlɛn, ne tɛ u fe.', translation: 'Les deux comptes sont trop compliqués, je n\'en veux pas.', isCorrect: false },
        ],
        correctReaction: '"don ni bɔ" = déposer et retirer. Tu choisis le compte courant en justifiant !',
        hint: '"don" = déposer / "bɔ" = retirer / "mara" = garder / épargner',
      },
      {
        id: 's5',
        message: 'Ɲuman ! Jaabi ladon na, i ka wajibi fɛn damani don : i tɔgɔ sɛbɛn ni foto ni i ka so ɲɛfɛ sɛbɛn.',
        translation: 'Bien ! Pour ouvrir un compte, il faut : une pièce d\'identité, une photo et un justificatif de domicile.',
        options: [
          { id: 'a', text: 'Ne ka karta bɛ ne fɛ. Nga ne tɛ foto sɔrɔ sisan. Ne bɛ se ka foto ta kɔni ka na wa ?', translation: 'J\'ai ma carte d\'identité. Mais je n\'ai pas de photo. Je peux en prendre une et revenir ?', isCorrect: true },
          { id: 'b', text: 'Ne tɛ tɔgɔ sɔrɔ, ne tɔgɔ tɛ.', translation: 'Je n\'ai pas de nom, je n\'ai pas de nom.', isCorrect: false },
          { id: 'c', text: 'Fɛn nunu bɛɛ bɛ ne fɛ, nga ne tɛ jaabi fe tuma.', translation: 'J\'ai tous ces documents, mais je ne veux plus de compte.', isCorrect: false },
        ],
        correctReaction: '"wajibi" = obligatoire. "karta" = carte d\'identité. Tu gères la situation !',
        hint: '"wajibi" = nécessaire / "tɔgɔ sɛbɛn" = pièce d\'identité / "so ɲɛfɛ sɛbɛn" = justificatif de domicile',
      },
      {
        id: 's6',
        message: 'Ayi tɔrɔ tɛ ! Fotografi bɛ banki kɔfɛ dɔɔni, i bɛ se ka taa ka na joona.',
        translation: 'Pas de problème ! Il y a un photographe juste derrière, tu peux y aller et revenir vite.',
        options: [
          { id: 'a', text: 'Aw ni ce ! So ɲɛfɛ sɛbɛn fana, ne bɛ min don ? Ne bɛ so ɲɔgɔn na.', translation: 'Merci ! Pour le justificatif de domicile, qu\'est-ce que j\'apporte ? Je suis locataire.', isCorrect: true },
          { id: 'b', text: 'Fotografi ye jɔn ye ? Ne m\'a dɔn.', translation: 'C\'est qui le photographe ? Je ne le connais pas.', isCorrect: false },
          { id: 'c', text: 'Ne tɛ foto fe, foto tɛ wajibi.', translation: 'Je ne veux pas de photo, la photo n\'est pas obligatoire.', isCorrect: false },
        ],
        correctReaction: '"fotografi" = photographe. "so ɲɔgɔn na" = je suis locataire. Tu anticipes la suite !',
        hint: '"kɔfɛ" = derrière / "fotografi" = photographe / "so ɲɔgɔn na" = locataire',
      },
      {
        id: 's7',
        message: 'I bɛ se ka i ka so baaraden ka sɛbɛn ta, wala i bɛ se ka dɛmɛ sɛbɛn ta i ka so tigi fɛ.',
        translation: 'Tu peux apporter une quittance de loyer, ou une lettre de ton propriétaire.',
        options: [
          { id: 'a', text: 'Ayiwa, ne ka so tigi bɛ se ka sɛbɛn nin kɛ ne ye joona. Wari min bɛ wajibi jaabi ladon na ?', translation: 'D\'accord, mon propriétaire peut faire ça vite. Quel est le montant minimum pour ouvrir le compte ?', isCorrect: true },
          { id: 'b', text: 'Ne tɛ so tigi dɔn, ne bɛ so kɔnɔ i ko di.', translation: 'Je ne connais pas mon propriétaire, je vis dans la maison comme ça.', isCorrect: false },
          { id: 'c', text: 'So baaraden ye mun ye ? Ne m\'a ye fɔlɔ.', translation: 'C\'est quoi une quittance ? Je n\'en ai jamais vu.', isCorrect: false },
        ],
        correctReaction: '"so baaraden" = quittance. Tu règles le problème et demandes le dépôt minimum !',
        hint: '"so baaraden" = quittance de loyer / "so tigi" = propriétaire / "dɛmɛ sɛbɛn" = lettre',
      },
      {
        id: 's8',
        message: 'Jaabi ladon na, wari dɔgɔma ye wari kɛmɛ du ye. O ye wari kelen ye min bɛ to jaabi kɔnɔ tuma bɛɛ.',
        translation: 'Pour ouvrir un compte, le minimum est de mille francs. C\'est le montant qui doit toujours rester dans le compte.',
        options: [
          { id: 'a', text: 'Ayiwa, wari nin bɛ ne fɛ. Jaabi ladon kɛlen kɛ, waati juman kɔnɔ ne bɛ se ka karta sɔrɔ ?', translation: 'D\'accord, j\'ai cet argent. Une fois le compte ouvert, dans combien de temps j\'ai ma carte ?', isCorrect: true },
          { id: 'b', text: 'Wari kɛmɛ du ka bon, ne tɛ se ka o don.', translation: 'Mille francs c\'est trop, je ne peux pas mettre ça.', isCorrect: false },
          { id: 'c', text: 'Ne tɛ wari dɔn, wari bɛ min na sɔrɔ ?', translation: 'Je ne connais pas l\'argent, où est-ce qu\'on trouve de l\'argent ?', isCorrect: false },
        ],
        correctReaction: '"wari kɛmɛ du" = mille francs. "waati juman kɔnɔ" = dans combien de temps. Parfait !',
        hint: '"wari kɛmɛ du" = mille francs / "waati juman kɔnɔ" = dans combien de temps / "karta" = carte',
      },
      {
        id: 's9',
        message: 'Karta bɛ se ka ban don saba ni kɔnɔntɔn cɛ. An bɛ i weele telefɔni la a labɛnnen na.',
        translation: 'La carte peut être prête entre trois et sept jours. On t\'appellera quand elle sera prête.',
        options: [
          { id: 'a', text: 'Ɲuman ! Ne ka telefɔni nimɔrɔ bɛ nin ye. I bɛ se ka a sɛbɛn wa ?', translation: 'Bien ! Voici mon numéro de téléphone. Tu peux le noter ?', isCorrect: true },
          { id: 'b', text: 'Don saba ka jan kosɛbɛ, ne bɛ karta sɔrɔ sisan.', translation: 'Trois jours c\'est trop long, je veux la carte maintenant.', isCorrect: false },
          { id: 'c', text: 'Ne tɛ telefɔni sɔrɔ, ne tɛ nimɔrɔ dɔn.', translation: 'Je n\'ai pas de téléphone, je ne connais pas les chiffres.', isCorrect: false },
        ],
        correctReaction: '"telefɔni nimɔrɔ" = numéro de téléphone. "sɛbɛn" = noter / écrire. Super !',
        hint: '"ban" = être prêt / "weele" = appeler / "telefɔni nimɔrɔ" = numéro de téléphone',
      },
      {
        id: 's10',
        message: 'Ayi tɔrɔ tɛ, ne bɛ a sɛbɛn. Fɛn wɛrɛ bɛ wa min na i ka miiri kɛ ?',
        translation: 'Pas de problème, je le note. Y a-t-il autre chose ?',
        options: [
          { id: 'a', text: 'Ayi, o bɛɛ ye ɲi. Ne bɛ taa joona ka fɛn nunu bɛɛ lajɛ ka na. Aw ni ce i ka dɛmɛ kosɛbɛ !', translation: 'Non, tout va bien. Je vais préparer tous ces documents et revenir. Merci beaucoup !', isCorrect: true },
          { id: 'b', text: 'Ayi, ne bɛ jaabi fila ladon sisan.', translation: 'Non, je veux ouvrir deux comptes maintenant.', isCorrect: false },
          { id: 'c', text: 'Ne bɛ wari bɔ sisan, ne ka jaabi kɔnɔ wari bɛ joli ?', translation: 'Je veux retirer de l\'argent, combien y a-t-il dans mon compte ?', isCorrect: false },
        ],
        correctReaction: '"lajɛ" = préparer / rassembler. Tu conclues avec gratitude et méthode !',
        hint: '"fɛn wɛrɛ" = autre chose / "lajɛ" = préparer / "kosɛbɛ" = beaucoup',
      },
      {
        id: 's11',
        message: 'I ni ce fana ! An bɛ kɔnɔ i kɔfɛ. Ala k\'i to kɛnɛ !',
        translation: 'Merci aussi ! On vous attend. Que Dieu te garde !',
        options: [
          { id: 'a', text: 'Ala k\'an to kɛnɛ bɛɛ ! Kɔsɛgɛn !', translation: 'Que Dieu nous garde tous ! À bientôt !', isCorrect: true },
          { id: 'b', text: 'Ne tɛ kɔsɛgɛn, banki tɛ ɲi.', translation: 'Je ne reviendrai pas, la banque n\'est pas bien.', isCorrect: false },
          { id: 'c', text: 'Ala tɛ kɛnɛ, ne bɛ kɛnɛ.', translation: 'Dieu n\'attend pas, c\'est moi qui attends.', isCorrect: false },
        ],
        correctReaction: '🎉 Félicitations ! Tu as ouvert un compte en bambara comme un vrai Bamakois !',
      },
    ],
  },
];

//── Lingala ──────────────────────────────────────────────────────────────────
export const LINGALA_CONVERSATIONS: Conversation[] = [

  // ── Au marché ──────────────────────────────────────────────────────────────
  {
    id: 'marche-la',
    title: 'Au marché',
    context: '🏪 Tu es au marché à Kinshasa.',
    icon: '🏪',
    difficulty: 'débutant',
    character: { name: 'Bosco', emoji: '🧑🏿' },
    xpReward: 30,
    steps: [
      {
        id: 's1',
        message: 'Mbote !',
        translation: 'Bonjour !',
        options: [
          { id: 'a', text: 'Mbote !', translation: 'Bonjour !', isCorrect: true },
          { id: 'b', text: 'Matondo.', translation: 'Merci.', isCorrect: false },
          { id: 'c', text: 'Nakende.', translation: 'Je pars.', isCorrect: false },
          { id: 'd', text: 'Nazali awa.', translation: 'Je suis ici.', isCorrect: false },
        ],
        // correctReaction = réponse du vendeur après le bonjour → enchaîne naturellement
        correctReaction: 'Mbote ! Ozali malamu ?',
      },
      {
        id: 's2',
        message: 'Ozali malamu ?',
        translation: 'Tu vas bien ?',
        options: [
          { id: 'a', text: 'Nazali malamu, matondo. Bongo yo ?', translation: 'Je vais bien, merci. Et toi ?', isCorrect: true },
          { id: 'b', text: 'Nalingi kosomba.', translation: 'Je veux acheter.', isCorrect: false },
          { id: 'c', text: 'Nzela.', translation: 'Route.', isCorrect: false },
          { id: 'd', text: 'Nakende na ndako.', translation: 'Je rentre.', isCorrect: false },
        ],
        // Vendeur répond à "et toi ?" et enchaîne sur le but de la visite
        correctReaction: 'Ngai mpe nazali malamu, matondo ! Yaka, yaka — olingi kosomba nini ?',
      },
      {
        id: 's3',
        message: 'Olingi nini lelo ?',
        translation: 'Tu veux quoi aujourd\'hui ?',
        options: [
          { id: 'a', text: 'Nalingi kosomba mbisi na loso.', translation: 'Je veux acheter du poisson et du riz.', isCorrect: true },
          { id: 'b', text: 'Nazali malamu.', translation: 'Je vais bien.', isCorrect: false },
          { id: 'c', text: 'Nkombo na ngai Bosco.', translation: 'Je m\'appelle Bosco.', isCorrect: false },
          { id: 'd', text: 'Nakende sikoyo.', translation: 'Je pars maintenant.', isCorrect: false },
        ],
        // Vendeur confirme qu'il a les deux et précise le type de poisson disponible
        correctReaction: 'Mbisi na loso — tozali na bino nyonso ! Kasi mbisi ya ndeke to mbisi ya mai ?',
      },
      {
        id: 's4',
        message: 'Olingi mbisi ya ndeke to mbisi ya mai ?',
        translation: 'Tu veux du poisson fumé ou du poisson frais ?',
        options: [
          { id: 'a', text: 'Nalingi mbisi ya mai.', translation: 'Je veux du poisson frais.', isCorrect: true },
          { id: 'b', text: 'Nalingi loso te.', translation: 'Je ne veux pas de riz.', isCorrect: false },
          { id: 'c', text: 'Nalingi mayi.', translation: 'Je veux de l\'eau.', isCorrect: false },
          { id: 'd', text: 'Mbisi ezali kitoko.', translation: 'Le poisson est beau.', isCorrect: false },
        ],
        // Vendeur valide et oriente vers la question suivante sur le riz
        correctReaction: 'Sawa ! Mbisi ya mai ezali frais lelo. Na loso — ya motene to ya pamba ?',
      },
      {
        id: 's5',
        message: 'Loso ya motene to loso ya pamba — olingi nini ?',
        translation: 'Riz complet ou riz blanc — tu veux quoi ?',
        options: [
          { id: 'a', text: 'Nalingi loso ya pamba.', translation: 'Je veux du riz blanc.', isCorrect: true },
          { id: 'b', text: 'Nalingi loso ya ndeke.', translation: 'Je veux du riz fumé.', isCorrect: false },
          { id: 'c', text: 'Loso ezali mabe.', translation: 'Le riz est mauvais.', isCorrect: false },
          { id: 'd', text: 'Nazali na nzala te.', translation: 'Je n\'ai pas faim.', isCorrect: false },
        ],
        // Vendeur demande la quantité souhaitée
        correctReaction: 'Malamu ! Okozwa boni ya loso — kilo moko to mibale ?',
      },
      {
        id: 's6',
        message: 'Okozwa boni ?',
        translation: 'Tu prends combien ?',
        options: [
          { id: 'a', text: 'Pesa ngai nkama mibale ya loso.', translation: 'Donne-moi 200g de riz.', isCorrect: true },
          { id: 'b', text: 'Nalingi mbisi te.', translation: 'Je ne veux pas de poisson.', isCorrect: false },
          { id: 'c', text: 'Ezali boni ?', translation: 'Ça coûte combien ?', isCorrect: false },
          { id: 'd', text: 'Nakende sikoyo.', translation: 'Je pars maintenant.', isCorrect: false },
        ],
        // Vendeur rassemble la commande et annonce le total
        correctReaction: 'Sawa ! Nakozwa bino nyonso. Zela moke...',
      },
      {
        id: 's7',
        message: 'Tala — mbisi na loso nyonso ezali awa. Ezali francs nkoto mibale.',
        translation: 'Voilà — le poisson et le riz sont là. C\'est 2000 francs en tout.',
        options: [
          { id: 'a', text: 'Okoki kokitisa moke ?', translation: 'Tu peux baisser un peu ?', isCorrect: true },
          { id: 'b', text: 'Nazali malamu.', translation: 'Je vais bien.', isCorrect: false },
          { id: 'c', text: 'Nalingi mbisi te.', translation: 'Je ne veux pas de poisson.', isCorrect: false },
          { id: 'd', text: 'Nakende sikoyo.', translation: 'Je pars maintenant.', isCorrect: false },
        ],
        // Vendeur accepte de baisser et annonce le nouveau prix
        correctReaction: 'Ahhh ! Ozali moninga ! Nakitisi moke — ezali nkoto moko na nkama mitano !',
      },
      {
        id: 's8',
        message: 'Ezali nkoto moko na nkama mitano. Sawa ?',
        translation: '1500 francs. D\'accord ?',
        options: [
          { id: 'a', text: 'Eee, nakofuta. Matondo mingi !', translation: 'Oui, je paie. Merci beaucoup !', isCorrect: true },
          { id: 'b', text: 'Ezali lisusu mingi !', translation: 'C\'est encore trop !', isCorrect: false },
          { id: 'c', text: 'Nalingi mbisi ya ndeke.', translation: 'Je veux du poisson fumé.', isCorrect: false },
          { id: 'd', text: 'Nalingi lisusu.', translation: 'Je veux encore.', isCorrect: false },
        ],
        correctReaction: '🎉 Malamu ! Ozali negotiator ya solo ! Kende malamu na mbisi na yo !',
      },
    ],
  },

  // ── Se présenter ───────────────────────────────────────────────────────────
  {
    id: 'presentation-la',
    title: 'Se présenter',
    context: '👋 Tu rencontres quelqu\'un à Kinshasa.',
    icon: '👋',
    difficulty: 'débutant',
    character: { name: 'Amina', emoji: '👩🏿' },
    xpReward: 25,
    steps: [
      {
        id: 's1',
        message: 'Mbote ! Nkombo na yo nani ?',
        translation: 'Bonjour ! Tu t\'appelles comment ?',
        options: [
          { id: 'a', text: 'Nkombo na ngai [prénom].', translation: 'Je m\'appelle [prénom].', isCorrect: true },
          { id: 'b', text: 'Nakende na ndako.', translation: 'Je pars.', isCorrect: false },
          { id: 'c', text: 'Nazali awa.', translation: 'Je suis ici.', isCorrect: false },
          { id: 'd', text: 'Nalingi mbisi.', translation: 'J\'aime le poisson.', isCorrect: false },
        ],
        correctReaction: 'Nkombo kitoko ! Nasepeli koyeba yo. Ngai nkombo na ngai Amina.',
      },
      {
        id: 's2',
        message: 'Ozali wuta wapi ?',
        translation: 'Tu viens d\'où ?',
        options: [
          { id: 'a', text: 'Nazali wuta Falansi.', translation: 'Je viens de France.', isCorrect: true },
          { id: 'b', text: 'Nazali na ndako.', translation: 'Je suis à la maison.', isCorrect: false },
          { id: 'c', text: 'Mbote mingi.', translation: 'Bonjour.', isCorrect: false },
          { id: 'd', text: 'Nakende sikoyo.', translation: 'Je pars maintenant.', isCorrect: false },
        ],
        // Réaction chaleureuse + curiosité sur la durée du séjour, sans répéter la question suivante
        correctReaction: 'Falansi ! Malamu mingi ! Kinshasa esepelisi yo ?',
      },
      {
        id: 's3',
        message: 'Ozali na Kinshasa tango boni ?',
        translation: 'Tu es à Kinshasa depuis combien de temps ?',
        options: [
          { id: 'a', text: 'Nakomi na Kinshasa sanza mibale.', translation: 'Je suis arrivé à Kinshasa il y a deux mois.', isCorrect: true },
          { id: 'b', text: 'Kinshasa ezali monene.', translation: 'Kinshasa est grande.', isCorrect: false },
          { id: 'c', text: 'Nalingi Kinshasa.', translation: 'J\'aime Kinshasa.', isCorrect: false },
          { id: 'd', text: 'Nazali awa.', translation: 'Je suis ici.', isCorrect: false },
        ],
        // Amina est impressionnée et enchaîne sur la langue, sans répéter la question mot pour mot
        correctReaction: 'Sanza mibale kaka ! Osali malamu ! Lingala na yo ezali kitoko déjà !',
      },
      {
        id: 's4',
        message: 'Oyebi koloba lingala ?',
        translation: 'Tu sais parler lingala ?',
        options: [
          { id: 'a', text: 'Moke, nazali koyekola.', translation: 'Un peu, j\'apprends.', isCorrect: true },
          { id: 'b', text: 'Ee, nayebi mingi.', translation: 'Oui, je sais beaucoup.', isCorrect: false },
          { id: 'c', text: 'Lingala ezali malamu.', translation: 'Le lingala est bien.', isCorrect: false },
          { id: 'd', text: 'Nazali awa.', translation: 'Je suis ici.', isCorrect: false },
        ],
        // Amina encourage et change de sujet vers le travail
        correctReaction: 'Malamu mingi ! Koyekola lingala ezali boni. Osali mosala nini awa ?',
      },
      {
        id: 's5',
        message: 'Osali mosala nini awa Kinshasa ?',
        translation: 'Tu fais quoi comme travail ici à Kinshasa ?',
        options: [
          { id: 'a', text: 'Nazali molakisi.', translation: 'Je suis enseignant(e).', isCorrect: true },
          { id: 'b', text: 'Nazali na nzala.', translation: 'J\'ai faim.', isCorrect: false },
          { id: 'c', text: 'Nakende na zando.', translation: 'Je vais au marché.', isCorrect: false },
          { id: 'd', text: 'Nalingi kolya.', translation: 'Je veux manger.', isCorrect: false },
        ],
        // Amina réagit avec chaleur et curiosité sur la famille
        correctReaction: 'Molakisi ! Mosala ya ntina mingi ! Ata bana ya Kinshasa basepeli. Ozali nzomi awa ?',
      },
      {
        id: 's6',
        message: 'Ozali na libota awa ?',
        translation: 'Tu as de la famille ici ?',
        options: [
          { id: 'a', text: 'Te, nazali moto moko. Libota na ngai ezali na Falansi.', translation: 'Non, je suis seul(e). Ma famille est en France.', isCorrect: true },
          { id: 'b', text: 'Nazali na nzala.', translation: 'J\'ai faim.', isCorrect: false },
          { id: 'c', text: 'Nalingi Kinshasa te.', translation: 'Je n\'aime pas Kinshasa.', isCorrect: false },
          { id: 'd', text: 'Nakende na ndako.', translation: 'Je vais à la maison.', isCorrect: false },
        ],
        // Amina compatit et propose de l'aide
        correctReaction: 'Ohhh ! Moto moko te — Kinshasa ekozala libota na yo ! Ozali na baninga awa ?',
      },
      {
        id: 's7',
        message: 'Ozali na moninga awa ?',
        translation: 'Tu as des amis ici ?',
        options: [
          { id: 'a', text: 'Nazali na baninga moke. Nalingi kozwa baninga mingi !', translation: 'J\'ai peu d\'amis. Je veux me faire plus d\'amis !', isCorrect: true },
          { id: 'b', text: 'Nakende sikoyo.', translation: 'Je pars maintenant.', isCorrect: false },
          { id: 'c', text: 'Nalingi kolya.', translation: 'Je veux manger.', isCorrect: false },
          { id: 'd', text: 'Nazali mpe.', translation: 'Moi aussi.', isCorrect: false },
        ],
        // Amina s'offre comme amie et pose une dernière question intime
        correctReaction: 'Sawa ! Ngai nakoki kozala moninga na yo ya solosolo ! Na komona yo, nayebi te — ozali na mibu boni ?',
      },
      {
        id: 's8',
        message: 'Ozali na mibu boni ?',
        translation: 'Tu as quel âge ?',
        options: [
          { id: 'a', text: 'Nazali na mibu tuku mibale na mitano.', translation: 'J\'ai 25 ans.', isCorrect: true },
          { id: 'b', text: 'Nazali na ndako.', translation: 'Je suis à la maison.', isCorrect: false },
          { id: 'c', text: 'Nalingi kosomba.', translation: 'Je veux acheter.', isCorrect: false },
          { id: 'd', text: 'Nkombo na ngai malamu.', translation: 'Mon nom est bon.', isCorrect: false },
        ],
        correctReaction: 'Ngai nazali na mibu tuku misato ! Tozali pene ! Tokozala baninga malamu.',
      },
      {
        id: 's9',
        message: 'Nasepeli koyeba yo ! Tokozala baninga malamu !',
        translation: 'Ravi de te rencontrer ! On sera de bons amis !',
        options: [
          { id: 'a', text: 'Nasepeli mpe ! Kende malamu !', translation: 'Moi aussi ! Bonne route !', isCorrect: true },
          { id: 'b', text: 'Nakende lisusu.', translation: 'Je pars encore.', isCorrect: false },
          { id: 'c', text: 'Nazali na nzala.', translation: 'J\'ai faim.', isCorrect: false },
          { id: 'd', text: 'Nalingi kosomba.', translation: 'Je veux acheter.', isCorrect: false },
        ],
        correctReaction: '👏 Malamu ! Oyebi komilakisa na lingala ya solosolo !',
      },
    ],
  },

  // ── Au restaurant ──────────────────────────────────────────────────────────
  {
    id: 'restaurant-la',
    title: 'Au restaurant',
    context: '🍽️ Tu es dans un restaurant à Kinshasa.',
    icon: '🍽️',
    difficulty: 'débutant',
    character: { name: 'Mama Ngozi', emoji: '👩🏿‍🍳' },
    xpReward: 30,
    steps: [
      {
        id: 's1',
        message: 'Mbote ! Ozali na nzala ?',
        translation: 'Bonjour ! Tu as faim ?',
        options: [
          { id: 'a', text: 'Eee, nazali na nzala mingi. Bozali na nini ?', translation: 'Oui, j\'ai très faim. Vous avez quoi ?', isCorrect: true },
          { id: 'b', text: 'Nakende sikoyo.', translation: 'Je pars maintenant.', isCorrect: false },
          { id: 'c', text: 'Nazali malamu.', translation: 'Je vais bien.', isCorrect: false },
          { id: 'd', text: 'Nazali awa.', translation: 'Je suis ici.', isCorrect: false },
        ],
        // Mama énumère les plats — s2 demandera de choisir parmi eux
        correctReaction: 'Yaka fanda ! Lelo tozali na lituma, pondu, na moambe. Tala biloko ya lelo !',
      },
      {
        id: 's2',
        message: 'Lituma, pondu, moambe ezali. Olingi nini ?',
        translation: 'Il y a foutou, pondu, moambe. Tu veux quoi ?',
        options: [
          { id: 'a', text: 'Nalingi moambe na loso.', translation: 'Je veux moambe avec riz.', isCorrect: true },
          { id: 'b', text: 'Nalingi lituma te.', translation: 'Je ne veux pas de foutou.', isCorrect: false },
          { id: 'c', text: 'Nakende sikoyo.', translation: 'Je pars maintenant.', isCorrect: false },
          { id: 'd', text: 'Mbisi ezali ?', translation: 'Il y a du poisson ?', isCorrect: false },
        ],
        // Mama précise les options de protéines avant que l'apprenant choisisse
        correctReaction: 'Moambe na loso — choix ya bonne ! Olingi mbisi to nyama na moambe ?',
      },
      {
        id: 's3',
        message: 'Olingi mbisi to nyama na moambe na yo ?',
        translation: 'Tu veux du poisson ou de la viande avec ton moambe ?',
        options: [
          { id: 'a', text: 'Nalingi mbisi na moambe.', translation: 'Je veux du poisson avec le moambe.', isCorrect: true },
          { id: 'b', text: 'Nalingi nyama.', translation: 'Je veux de la viande.', isCorrect: false },
          { id: 'c', text: 'Nazali na nzala te.', translation: 'Je n\'ai pas faim.', isCorrect: false },
          { id: 'd', text: 'Nalingi loso ya pamba.', translation: 'Je veux du riz blanc.', isCorrect: false },
        ],
        // Mama enchaîne naturellement sur la boisson
        correctReaction: 'Mbisi ezali ya malamu lelo ! Na boisson — olingi nini ?',
      },
      {
        id: 's4',
        message: 'Olingi mayi to masanga ?',
        translation: 'Tu veux de l\'eau ou de la bière ?',
        options: [
          { id: 'a', text: 'Nalingi mayi, matondo.', translation: 'Je veux de l\'eau, merci.', isCorrect: true },
          { id: 'b', text: 'Nalingi masanga mibale.', translation: 'Je veux deux bières.', isCorrect: false },
          { id: 'c', text: 'Nakende sikoyo.', translation: 'Je pars maintenant.', isCorrect: false },
          { id: 'd', text: 'Nalingi susu.', translation: 'Je veux du poulet.', isCorrect: false },
        ],
        // Mama part préparer — s5 est le moment où elle revient avec la commande
        correctReaction: 'Malamu ! Mayi ezali malamu po na nzoto. Zela moke, nakoya na biloko na yo !',
      },
      {
        id: 's5',
        message: 'Tala ! Biloko na yo ezali awa.',
        translation: 'Voilà ! Voici ta commande.',
        options: [
          { id: 'a', text: 'Matondo mingi, Mama ! Ezali kitoko !', translation: 'Merci beaucoup, Mama ! Ça a l\'air bon !', isCorrect: true },
          { id: 'b', text: 'Ezali mabe.', translation: 'C\'est mauvais.', isCorrect: false },
          { id: 'c', text: 'Nalingi lisusu.', translation: 'Je veux encore.', isCorrect: false },
          { id: 'd', text: 'Nakende sikoyo.', translation: 'Je pars maintenant.', isCorrect: false },
        ],
        // Mama souhaite bon appétit — s6 se passe après le repas
        correctReaction: 'Bolei bolamu, mwana ! Nayeba soki ezali malamu.',
      },
      {
        id: 's6',
        message: 'Ezalaki malamu ?',
        translation: 'C\'était bon ?',
        options: [
          { id: 'a', text: 'Eee, ezalaki malamu mingi ! Mama azali mobeki ya ntina !', translation: 'Oui, c\'était très bon ! Mama est une vraie cuisinière !', isCorrect: true },
          { id: 'b', text: 'Ezalaki mabe.', translation: 'C\'était mauvais.', isCorrect: false },
          { id: 'c', text: 'Nalingi lisusu.', translation: 'Je veux encore.', isCorrect: false },
          { id: 'd', text: 'Nakende sikoyo.', translation: 'Je pars maintenant.', isCorrect: false },
        ],
        // Mama est touchée et propose de payer — s7 pose la question du moment du paiement
        correctReaction: 'Matondo mingi, mwana ! Nasepeli. Fanda moke — okoki kofuta tango olingi.',
      },
      {
        id: 's7',
        message: 'Olingi kofuta sikoyo ?',
        translation: 'Tu veux payer maintenant ?',
        options: [
          { id: 'a', text: 'Ee, nakofuta. Ezali boni nyonso ?', translation: 'Oui, je paie. C\'est combien en tout ?', isCorrect: true },
          { id: 'b', text: 'Nalingi lisusu moko.', translation: 'Je veux encore une portion.', isCorrect: false },
          { id: 'c', text: 'Nakende sikoyo.', translation: 'Je pars maintenant.', isCorrect: false },
          { id: 'd', text: 'Ezalaki mabe.', translation: 'C\'était mauvais.', isCorrect: false },
        ],
        // Mama annonce le prix — s8 confirme le paiement
        correctReaction: 'Moambe, loso, mbisi, na mayi — ezali francs nkoto mibale.',
      },
      {
        id: 's8',
        message: 'Ezali francs nkoto mibale. Okoki kofuta ?',
        translation: '2000 francs. Tu peux payer ?',
        options: [
          { id: 'a', text: 'Eee, nakofuta. Matondo mingi ! Nakoya lisusu !', translation: 'Oui, je paie. Merci ! Je reviendrai !', isCorrect: true },
          { id: 'b', text: 'Ezali mingi !', translation: 'C\'est trop !', isCorrect: false },
          { id: 'c', text: 'Nakende sikoyo.', translation: 'Je pars maintenant.', isCorrect: false },
          { id: 'd', text: 'Nalingi lisusu.', translation: 'Je veux encore.', isCorrect: false },
        ],
        correctReaction: '🍽️ Malamu ! Toyei kozela yo ! Oyebi ko-commander na lingala !',
      },
    ],
  },

  // ── Dans le bus ────────────────────────────────────────────────────────────
  {
    id: 'bus-la',
    title: 'Dans le bus',
    context: '🚌 Tu montes dans un bus à Kinshasa.',
    icon: '🚌',
    difficulty: 'débutant',
    character: { name: 'Receveur', emoji: '🧑🏿' },
    xpReward: 30,
    steps: [
      {
        id: 's1',
        message: 'Mbote ! Okende wapi ?',
        translation: 'Bonjour ! Tu vas où ?',
        options: [
          { id: 'a', text: 'Nakende na Gombe.', translation: 'Je vais à Gombe.', isCorrect: true },
          { id: 'b', text: 'Nasili koya.', translation: 'Je viens d\'arriver.', isCorrect: false },
          { id: 'c', text: 'Nakende te.', translation: 'Je ne pars pas.', isCorrect: false },
          { id: 'd', text: 'Nazali awa.', translation: 'Je suis ici.', isCorrect: false },
        ],
        // Receveur confirme que le bus va à Gombe ET donne le prix directement
        correctReaction: 'Sawa ! Bus oyo akende na Gombe. Mbongo ya transport ezali francs nkama mitano.',
      },
      {
        id: 's2',
        message: 'Ezali francs nkama mitano. Okoki kofuta ?',
        translation: 'C\'est 500 francs. Tu peux payer ?',
        options: [
          { id: 'a', text: 'Sawa, nakofuta.', translation: 'D\'accord, je paie.', isCorrect: true },
          { id: 'b', text: 'Ezali mingi !', translation: 'C\'est trop !', isCorrect: false },
          { id: 'c', text: 'Nazali na mbongo te.', translation: 'Je n\'ai pas d\'argent.', isCorrect: false },
          { id: 'd', text: 'Mbongo boni ?', translation: 'C\'est combien ?', isCorrect: false },
        ],
        // Receveur encaisse et souhaite un bon trajet
        correctReaction: 'Matondo ! Fanda malamu. Bus ekobanda sikoyo.',
      },
      {
        id: 's3',
        message: 'Oza wapi na Gombe ?',
        translation: 'Tu vas où exactement à Gombe ?',
        options: [
          { id: 'a', text: 'Nakende na boulevard du 30 juin.', translation: 'Je vais au boulevard du 30 juin.', isCorrect: true },
          { id: 'b', text: 'Nazali na ndako.', translation: 'Je suis à la maison.', isCorrect: false },
          { id: 'c', text: 'Nakende na zando.', translation: 'Je vais au marché.', isCorrect: false },
          { id: 'd', text: 'Nalingi kobima.', translation: 'Je veux descendre.', isCorrect: false },
        ],
        // Receveur estime le temps de trajet
        correctReaction: 'Boulevard du 30 juin — tokokoma kuna na miniti tuku mibale. Fanda malamu !',
      },
      {
        id: 's4',
        message: 'Ozali na Kinshasa tango boni ?',
        translation: 'Tu es à Kinshasa depuis combien de temps ?',
        options: [
          { id: 'a', text: 'Nakomi sanza mibale.', translation: 'Je suis arrivé il y a deux mois.', isCorrect: true },
          { id: 'b', text: 'Nakende sikoyo.', translation: 'Je pars maintenant.', isCorrect: false },
          { id: 'c', text: 'Ezali mosika.', translation: 'C\'est loin.', isCorrect: false },
          { id: 'd', text: 'Ezali pene.', translation: 'C\'est proche.', isCorrect: false },
        ],
        // Réaction amusée du receveur, curiosité sur l\'impression de la ville
        correctReaction: 'Sanza mibale kaka ! Oza mopaya encore ! Kinshasa esepelisi yo ?',
      },
      {
        id: 's5',
        message: 'Kinshasa esepelisi yo ?',
        translation: 'Kinshasa te plaît ?',
        options: [
          { id: 'a', text: 'Ee, nalingi Kinshasa mingi !', translation: 'Oui, j\'aime beaucoup Kinshasa !', isCorrect: true },
          { id: 'b', text: 'Nalingi kobima.', translation: 'Je veux descendre.', isCorrect: false },
          { id: 'c', text: 'Nazali na nzala.', translation: 'J\'ai faim.', isCorrect: false },
          { id: 'd', text: 'Nakende na ndako.', translation: 'Je vais à la maison.', isCorrect: false },
        ],
        // Receveur annonce l\'arrivée — s6 est le moment de descendre
        correctReaction: 'Kinshasa ezali mboka ya esengo ! Tala — tokomi ! Boulevard du 30 juin awa !',
      },
      {
        id: 's6',
        message: 'Tokomi ! Gombe awa. Kobima malamu !',
        translation: 'On est arrivé ! Voilà Gombe. Descends bien !',
        options: [
          { id: 'a', text: 'Matondo mingi ! Kende malamu !', translation: 'Merci beaucoup ! Bonne route !', isCorrect: true },
          { id: 'b', text: 'Nakende lisusu.', translation: 'Je pars encore.', isCorrect: false },
          { id: 'c', text: 'Nazali malamu.', translation: 'Je vais bien.', isCorrect: false },
          { id: 'd', text: 'Ezali mosika.', translation: 'C\'est loin.', isCorrect: false },
        ],
        correctReaction: '🚌 Bravo ! Oyebi kotambola na bus ya Kinshasa na lingala !',
      },
    ],
  },

  // ── Acheter des vêtements ──────────────────────────────────────────────────
  {
    id: 'shopping-la',
    title: 'Acheter des vêtements',
    context: '👕 Tu entres dans une boutique de vêtements à Kinshasa.',
    icon: '👕',
    difficulty: 'débutant',
    character: { name: 'Vendeur', emoji: '🧑🏿' },
    xpReward: 30,
    steps: [
      {
        id: 's1',
        message: 'Mbote ! Olingi nini ?',
        translation: 'Bonjour ! Tu veux quoi ?',
        options: [
          { id: 'a', text: 'Nalingi kosomba kazaka.', translation: 'Je veux acheter une veste.', isCorrect: true },
          { id: 'b', text: 'Nalingi kolya.', translation: 'Je veux manger.', isCorrect: false },
          { id: 'c', text: 'Nakende sikoyo.', translation: 'Je pars maintenant.', isCorrect: false },
          { id: 'd', text: 'Nazali awa.', translation: 'Je suis ici.', isCorrect: false },
        ],
        // Vendeur précise la question homme/femme — s2 pose cette question
        correctReaction: 'Kazaka ! Malamu. Tozali na mingi. Ya mibali to ya basi ?',
      },
      {
        id: 's2',
        message: 'Olingi kazaka ya mibali to ya basi ?',
        translation: 'Tu veux une veste homme ou femme ?',
        options: [
          { id: 'a', text: 'Nalingi kazaka ya mibali.', translation: 'Je veux une veste homme.', isCorrect: true },
          { id: 'b', text: 'Nalingi kazaka ya basi.', translation: 'Je veux une veste femme.', isCorrect: false },
          { id: 'c', text: 'Nazali awa.', translation: 'Je suis ici.', isCorrect: false },
          { id: 'd', text: 'Nalingi loso.', translation: 'Je veux du riz.', isCorrect: false },
        ],
        // Vendeur montre quelque chose — s3 demande l'avis du client
        correctReaction: 'Sawa ! Tala oyo — ekomi sika. Ndenge nini ?',
      },
      {
        id: 's3',
        message: 'Ezali malamu ?',
        translation: 'C\'est bien ?',
        options: [
          { id: 'a', text: 'Ee, ezali kitoko ! Kasi bozali na rangi nini ?', translation: 'Oui, c\'est joli ! Mais vous avez quelle couleur ?', isCorrect: true },
          { id: 'b', text: 'Ezali mabe.', translation: 'C\'est mauvais.', isCorrect: false },
          { id: 'c', text: 'Ezali monene.', translation: 'C\'est grand.', isCorrect: false },
          { id: 'd', text: 'Ezali mokuse.', translation: 'C\'est petit.', isCorrect: false },
        ],
        // Vendeur liste les couleurs disponibles — s4 demande de choisir
        correctReaction: 'Tozali na rangi misato : moindo, pembe, na motane. Olingi nini ?',
      },
      {
        id: 's4',
        message: 'Tozali na rangi ya moindo, pembe, na motane. Olingi nini ?',
        translation: 'On a noir, blanc, et marron. Tu veux quoi ?',
        options: [
          { id: 'a', text: 'Nalingi ya moindo.', translation: 'Je veux le noir.', isCorrect: true },
          { id: 'b', text: 'Nalingi ya motane.', translation: 'Je veux le marron.', isCorrect: false },
          { id: 'c', text: 'Nalingi ya pembe.', translation: 'Je veux le blanc.', isCorrect: false },
          { id: 'd', text: 'Nalingi ya moindo te.', translation: 'Je ne veux pas le noir.', isCorrect: false },
        ],
        // Vendeur cherche la bonne taille — s5 pose la question de la taille
        correctReaction: 'Moindo ! Choix malamu. Tala moko awa — nomboro na yo ?',
      },
      {
        id: 's5',
        message: 'Nomboro na yo ezali boni ?',
        translation: 'C\'est quelle taille pour toi ?',
        options: [
          { id: 'a', text: 'Nazali na taille M.', translation: 'Je fais du M.', isCorrect: true },
          { id: 'b', text: 'Nazali monene.', translation: 'Je suis grand.', isCorrect: false },
          { id: 'c', text: 'Nalingi ya monene.', translation: 'Je veux du grand.', isCorrect: false },
          { id: 'd', text: 'Nazali na nzala.', translation: 'J\'ai faim.', isCorrect: false },
        ],
        // Vendeur apporte la bonne taille et invite à essayer — s6 demande si ça va
        correctReaction: 'M ! Awa — ya moindo ya M. Lata moke, komona soki ezali malamu !',
      },
      {
        id: 's6',
        message: 'Ezali malamu na yo ?',
        translation: 'Ça te va bien ?',
        options: [
          { id: 'a', text: 'Ee, ezali malamu mingi ! Ezali boni ?', translation: 'Oui, ça me va très bien ! C\'est combien ?', isCorrect: true },
          { id: 'b', text: 'Ezali monene mingi.', translation: 'C\'est trop grand.', isCorrect: false },
          { id: 'c', text: 'Ezali mokuse mingi.', translation: 'C\'est trop petit.', isCorrect: false },
          { id: 'd', text: 'Nalingi rangi mosusu.', translation: 'Je veux une autre couleur.', isCorrect: false },
        ],
        // Vendeur annonce le prix — s7 permet de négocier
        correctReaction: 'Ezali malamu na yo ! Kazaka oyo ebeli francs nkoto moko.',
      },
      {
        id: 's7',
        message: 'Ezali francs nkoto moko.',
        translation: 'Ça coûte 1000 francs.',
        options: [
          { id: 'a', text: 'Okoki kokitisa ?', translation: 'Tu peux baisser le prix ?', isCorrect: true },
          { id: 'b', text: 'Ezali malamu.', translation: 'C\'est bien.', isCorrect: false },
          { id: 'c', text: 'Nalingi rangi mosusu.', translation: 'Je veux une autre couleur.', isCorrect: false },
          { id: 'd', text: 'Nalingi lisusu.', translation: 'Je veux encore.', isCorrect: false },
        ],
        // Vendeur accepte de baisser et annonce le nouveau prix — s8 conclut
        correctReaction: 'Oooh ! Ozali ya nzela. Nakitisi mwa moke — ezali francs nkama mwambe !',
      },
      {
        id: 's8',
        message: 'Ezali francs nkama mwambe. Sawa ?',
        translation: '800 francs. D\'accord ?',
        options: [
          { id: 'a', text: 'Sawa ! Nakofuta. Matondo mingi !', translation: 'D\'accord ! Je paie. Merci beaucoup !', isCorrect: true },
          { id: 'b', text: 'Ezali lisusu mingi !', translation: 'C\'est encore trop !', isCorrect: false },
          { id: 'c', text: 'Nalingi kazaka mosusu.', translation: 'Je veux une autre veste.', isCorrect: false },
          { id: 'd', text: 'Nakende sikoyo.', translation: 'Je pars maintenant.', isCorrect: false },
        ],
        correctReaction: '🛍️ Bravo ! Ozali negotiator ya solo na lingala ! Kende malamu !',
      },
    ],
  },
];


     

// ─── Map langue → conversations ──────────────────────────────────────────────
export const CONVERSATIONS_BY_LANGUAGE: Record<string, Conversation[]> = {
  wf: WOLOF_CONVERSATIONS,
  ba : BAMBARA_CONVERSATIONS,
  la: LINGALA_CONVERSATIONS,


 
};